import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { WorkPermit, Site, Sector, Area, User, Employee, ApprovalConfig as ApprovalConfigApi, RiskAssessment, EPP as EPPApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage, useSite, useCompany } from "../../../../contexts";
import { isMaster, isAdmin } from "../../../../utils/checkPermission";
import { BasicModal } from "../../../Shared";
import { SiteForm } from "../../Sites/SiteForm/SiteForm";
import { SectorForm } from "../../Sectors/SectorForm/SectorForm";
import "./WorkPermitForm.scss";

const wpController             = new WorkPermit();
const siteController           = new Site();
const sectorController         = new Sector();
const areaController           = new Area();
const userController           = new User();
const employeeController       = new Employee();
const approvalConfigController = new ApprovalConfigApi();
const riskController           = new RiskAssessment();
const eppApiController         = new EPPApi();

const PAIS_OPTIONS = [
  { value: "AR", label: "🇦🇷 Argentina" },
  { value: "BO", label: "🇧🇴 Bolivia" },
  { value: "BR", label: "🇧🇷 Brasil" },
  { value: "CL", label: "🇨🇱 Chile" },
  { value: "CO", label: "🇨🇴 Colombia" },
  { value: "CR", label: "🇨🇷 Costa Rica" },
  { value: "DO", label: "🇩🇴 Rep. Dominicana" },
  { value: "EC", label: "🇪🇨 Ecuador" },
  { value: "GT", label: "🇬🇹 Guatemala" },
  { value: "MX", label: "🇲🇽 México" },
  { value: "NI", label: "🇳🇮 Nicaragua" },
  { value: "PA", label: "🇵🇦 Panamá" },
  { value: "PE", label: "🇵🇪 Perú" },
  { value: "PY", label: "🇵🇾 Paraguay" },
  { value: "SV", label: "🇸🇻 El Salvador" },
  { value: "UY", label: "🇺🇾 Uruguay" },
  { value: "VE", label: "🇻🇪 Venezuela" },
];

const PAIS_LEGAL = {
  AR: "Ley 19.587 (1972) · Dec. 351/79 · Autoridad: SRT (Superintendencia de Riesgos del Trabajo)",
  BO: "Ley General del Trabajo · DS 16998 · Autoridad: Min. Trabajo, Empleo y Previsión Social",
  BR: "CLT Cap.V · Lei 6.514/77 · Portaria MTb 3.214/78 (NRs) · Autoridad: MTE (Ministério do Trabalho)",
  CL: "Ley 16.744 · DS 594 · DS 40 · Autoridad: SUSESO + Mutuales (ACHS / MUSEG / IST)",
  CO: "Ley 1562/2012 · Dec.1072/2015 · Res.0312/2019 · Autoridad: Min. Trabajo + ARL",
  CR: "Código Trabajo Tít.IV · Ley 6.727 · Autoridad: MTSS + Consejo de Salud Ocupacional",
  DO: "Reg.522-06 · Código Trabajo · Ley 87-01 · Autoridad: Min. Trabajo + IDOPPRIL",
  EC: "Decreto 2393 (1986) · Código Trabajo · Res.CD-513 · Autoridad: Min. Trabajo + IESS",
  GT: "Código de Trabajo + AG 229-2014 · Autoridad: MINTRAB + IGSS",
  MX: "LFT · Reglamento Federal de SST 2014 · Autoridad: STPS",
  NI: "Ley 618 · Dec.96-2007 · Autoridad: MITRAB + INSS",
  PA: "Código Trabajo + DE 2/2008 · Res.CSS 45.588-2011 · Autoridad: MITRADEL + CSS",
  PE: "Ley 29783 (2011) · DS 005-2012-TR · Autoridad: MTPE + SUNAFIL",
  PY: "Ley 213/93 · Dec.14.390/92 · Autoridad: MTESS",
  SV: "Ley General de Prevención de Riesgos (Dec.254/2010) · Autoridad: MTPS",
  UY: "Ley 5.032 (1914) · Dec.406/88 · Dec.291/07 · Autoridad: MTSS + IGTSS",
  VE: "LOPCYMAT (2005) + Reglamento Parcial · Autoridad: INPSASEL",
};

const PAIS_ASEGURADORA = {
  AR: "ART (Ley 24.557)",
  BO: "CNS (Caja Nacional de Salud)",
  BR: "INSS + CAT + eSocial",
  CL: "Mutualidad de Empleadores (ACHS / MUSEG / IST)",
  CO: "ARL (Dec.1295/94)",
  CR: "INS (Instituto Nacional de Seguros)",
  DO: "IDOPPRIL (Seguro Riesgos Laborales)",
  EC: "IESS (SGRT)",
  GT: "IGSS",
  MX: "IMSS + Seguro de Riesgos de Trabajo",
  NI: "INSS (Riesgos Profesionales)",
  PA: "CSS (Riesgos Profesionales)",
  PE: "SCTR (Seguro Complementario Trabajo de Riesgo)",
  PY: "IPS (Instituto de Previsión Social)",
  SV: "ISSS",
  UY: "BSE (Banco de Seguros del Estado)",
  VE: "INPSASEL + Tesorería SS",
};

const PAIS_DOCS_ITEMS = {
  AR: ["Constancia ART", "F.931 AFIP (aportes)", "Programa de Seguridad (Res. SRT 51/97)"],
  BO: ["DS 16998", "Res. MTEPS 938/14"],
  BR: ["PGR (Programa de Gerenciamento de Riscos)", "PCMSO", "CAGED / eSocial"],
  CL: ["Ley 20.123 — Subcontratación", "DS 76 (empresa principal)"],
  CO: ["SG-SST contratistas (Dec.1072 Art.2.2.4.6.28)", "Afiliación ARL vigente"],
  CR: ["CSO (Certificado Salud Ocupacional)", "Reglamento INS de Contratistas"],
  DO: ["Reg.522-06", "Certificación Min. Trabajo"],
  EC: ["Reglamento Subcontratación", "Res. CD-513 IESS"],
  GT: ["AG 229-2014", "Reglamento de Construcción"],
  MX: ["REPSE (Registro prestadoras de servicios)", "IMSS (alta trabajadores)", "Programa SST (NOM-030)"],
  NI: ["Ley 618 — Cap. Servicios contratados", "Afiliación INSS"],
  PA: ["CSS (Caja de Seguro Social)", "DE 2/2008"],
  PE: ["Ley 29783 Art.68 — responsabilidad solidaria", "SCTR vigente", "Plan SST subcontratistas"],
  PY: ["Ley 213/93", "Reglamentación MTESS"],
  SV: ["Dec.254/2010", "Reglamentos MTPS aplicables"],
  UY: ["Ley 18.099 — Subcontratación", "Dec.224/07 (retención pagos)", "Certificado BPS"],
  VE: ["LOPCYMAT", "Reglamento Parcial (responsabilidad solidaria)", "Registro INPSASEL"],
};

const DOC_FISCAL_PLACEHOLDER = {
  AR: "CUIT (ej: 30-12345678-9)",
  BR: "CNPJ (ej: 12.345.678/0001-90)",
  CL: "RUT (ej: 12.345.678-9)",
  CO: "NIT (ej: 900.123.456-7)",
  MX: "RFC (ej: ABC123456XYZ)",
  PE: "RUC (ej: 20123456789)",
  EC: "RUC (ej: 1234567890001)",
  VE: "RIF (ej: J-12345678-9)",
};

const TIPOS_OPTIONS = [
  { key: "altura",            label: "Altura" },
  { key: "caliente",          label: "Caliente" },
  { key: "espacio_confinado", label: "Espacio confinado" },
  { key: "electrico_loto",    label: "Eléctrico / LOTO" },
  { key: "izaje",             label: "Izaje" },
  { key: "quimicos",          label: "Químicos" },
  { key: "excavacion",        label: "Excavación" },
  { key: "caldera",           label: "Caldera" },
  { key: "demolicion",        label: "Demolición" },
  { key: "acceso_techo",      label: "Acceso a techo" },
  { key: "radiaciones",       label: "Radiaciones" },
];

const NORMA_POR_PAIS = {
  AR: {
    altura:            "Res.SRT 1338/96 · 905/15 (alt≥2.0m)",
    caliente:          "Dec.351/79 Cap.18 · Res.SRT 84/12",
    espacio_confinado: "Res.SRT 295/03 Anexo II",
    electrico_loto:    "Dec.351/79 Cap.14 · Res.AEA 95201",
    izaje:             "Dec.351/79 Cap.16",
    excavacion:        "Dec.911/96 Cap.13 (prof≥1.20m)",
    quimicos:          "Ley 24.051",
  },
  BO: {
    altura:            "DS 16998 Art.252 (alt≥1.8m)",
    caliente:          "DS 16998 Cap.IX",
    espacio_confinado: "DS 16998 Art.380",
    electrico_loto:    "DS 16998 + Reglamento eléctrico",
    izaje:             "DS 16998",
    excavacion:        "DS 16998 (prof≥1.50m)",
    quimicos:          "DS 16998 + Ley 1333",
  },
  BR: {
    altura:            "NR-35 (alt≥2.0m)",
    caliente:          "NR-34 + NR-18 Anexo",
    espacio_confinado: "NR-33",
    electrico_loto:    "NR-10",
    izaje:             "NR-11 + NR-18",
    excavacion:        "NR-18 (prof≥1.25m)",
    quimicos:          "NR-20 + NR-26",
  },
  CL: {
    altura:            "DS 594 Art.36 (alt≥1.8m)",
    caliente:          "DS 594 Art.42 · NCh 1411",
    espacio_confinado: "DS 594 Art.32 + Guía SUSESO",
    electrico_loto:    "NCh Elec. 4/2003 + DS 594",
    izaje:             "DS 594",
    excavacion:        "DS 594 + DS 50 (prof≥1.50m)",
    quimicos:          "DS 594 Art.66 + DS 78",
  },
  CO: {
    altura:            "Res.4272/2021 (alt≥2.0m)",
    caliente:          "Res.1409/2012 + Guía Min.Trabajo",
    espacio_confinado: "Res.0491/2020",
    electrico_loto:    "RETIE + Res.5018/2019",
    izaje:             "Res.5018/2019",
    excavacion:        "Res.1409/2012 + Dec.1072 (prof≥1.50m)",
    quimicos:          "Dec.1496/2018",
  },
  CR: {
    altura:            "INTE 31-09 (alt≥1.8m)",
    caliente:          "INTE 31-09 + INS",
    espacio_confinado: "INTE 31-08-04-2014",
    electrico_loto:    "NEC + Reglamento Eléctrico CR",
    izaje:             "INTE + INS",
    excavacion:        "Reg. Construcción (prof≥1.20m)",
    quimicos:          "Ley 8839",
  },
  DO: {
    altura:            "Reg.522-06 (alt≥1.8m)",
    caliente:          "Reg.522-06 Cap. Soldadura",
    espacio_confinado: "Reg.522-06 Cap. Confinado",
    electrico_loto:    "Reg.522-06 + RIE",
    izaje:             "Reg.522-06",
    excavacion:        "Reg.522-06",
    quimicos:          "Reg.522-06",
  },
  EC: {
    altura:            "Dec.2393 Art.49 + INEN (alt≥1.8m)",
    caliente:          "Dec.2393 Art.58 + INEN",
    espacio_confinado: "Dec.2393 Art.66",
    electrico_loto:    "Dec.2393 Art.40-46 + CIEC",
    izaje:             "Dec.2393",
    excavacion:        "Dec.2393 Art.61 (prof≥1.50m)",
    quimicos:          "Dec.2393",
  },
  GT: {
    altura:            "AG 229-2014 Cap.VIII (alt≥1.8m)",
    caliente:          "AG 229-2014 Cap. Soldadura",
    espacio_confinado: "AG 229-2014",
    electrico_loto:    "AG 229-2014 + NEC",
    izaje:             "AG 229-2014",
    excavacion:        "AG 229-2014 (prof≥1.50m)",
    quimicos:          "AG 229-2014",
  },
  MX: {
    altura:            "NOM-009-STPS-2011 (alt≥1.8m)",
    caliente:          "NOM-027-STPS-2008",
    espacio_confinado: "NOM-033-STPS-2015",
    electrico_loto:    "NOM-029-STPS-2011",
    izaje:             "NOM-006-STPS",
    excavacion:        "NOM-031-STPS (prof≥1.50m)",
    quimicos:          "NOM-005 + NOM-018 (SGA)",
  },
  NI: {
    altura:            "Ley 618 + Reg. Construcción (alt≥1.8m)",
    caliente:          "Ley 618",
    espacio_confinado: "Ley 618",
    electrico_loto:    "Ley 618 + CIEN",
    izaje:             "Ley 618",
    excavacion:        "Ley 618 (prof≥1.50m)",
    quimicos:          "Ley 618",
  },
  PA: {
    altura:            "DE 2/2008 + Res.CSS 45.588 (alt≥1.8m)",
    caliente:          "Res.CSS",
    espacio_confinado: "Res.CSS 45.588-2011",
    electrico_loto:    "RIE + Res.CSS",
    izaje:             "DE 2/2008",
    excavacion:        "DE 2/2008 (prof≥1.50m)",
    quimicos:          "Ley 36/96",
  },
  PE: {
    altura:            "RM 050-2013-TR + NTP 350.107 (alt≥1.8m)",
    caliente:          "DS 024-2016-EM · RM 111-2013-MEM",
    espacio_confinado: "RM 050-2013-TR + DS 005-2012-TR",
    electrico_loto:    "RM 111-2013-MEM",
    izaje:             "RM 050 + G-050",
    excavacion:        "G-050 (prof≥1.50m)",
    quimicos:          "Ley 28256",
  },
  PY: {
    altura:            "Dec.14.390/92 Cap.XI (alt≥2.0m)",
    caliente:          "Dec.14.390/92 Cap.X",
    espacio_confinado: "Dec.14.390/92",
    electrico_loto:    "Dec.14.390/92 + ANDE",
    izaje:             "Dec.14.390/92",
    excavacion:        "Dec.14.390/92 (prof≥1.50m)",
    quimicos:          "Dec.14.390/92",
  },
  SV: {
    altura:            "Reg. General PRL (alt≥1.8m)",
    caliente:          "Reg. General PRL",
    espacio_confinado: "Reg. General PRL",
    electrico_loto:    "Reg. General PRL + NEC",
    izaje:             "Reg. General PRL + Construcción",
    excavacion:        "Reg. General PRL + Construcción (prof≥1.50m)",
    quimicos:          "Reg. General PRL + Construcción",
  },
  UY: {
    altura:            "Dec.406/88 + Dec.125/14 (alt≥2.0m)",
    caliente:          "Dec.406/88 Cap.X",
    espacio_confinado: "Dec.406/88 + protocolos BSE",
    electrico_loto:    "Dec.406/88 + UTE",
    izaje:             "Dec.406/88",
    excavacion:        "Dec.125/14 (prof≥1.50m)",
    quimicos:          "Dec.406/88 + Dec.307/09",
  },
  VE: {
    altura:            "COVENIN 2237 + LOPCYMAT (alt≥1.8m)",
    caliente:          "COVENIN 1056-91",
    espacio_confinado: "COVENIN 2238-2000",
    electrico_loto:    "COVENIN 2249 + LOPCYMAT",
    izaje:             "COVENIN",
    excavacion:        "COVENIN 2245 (prof≥1.50m)",
    quimicos:          "COVENIN 2670",
  },
};

const STATUS_OPTIONS = [
  { value: "draft",        label: "Borrador" },
  { value: "requested",    label: "Solicitado" },
  { value: "pending_n2",   label: "Pendiente Nivel 2" },
  { value: "pending_n3",   label: "Pendiente Nivel 3" },
  { value: "pending_n4",   label: "Pendiente Nivel 4" },
  { value: "approved",     label: "Aprobado" },
  { value: "in_execution", label: "En ejecución" },
  { value: "verified",     label: "Verificado" },
  { value: "suspended",    label: "Suspendido" },
  { value: "paused",       label: "Pausado" },
  { value: "rejected",     label: "Rechazado" },
  { value: "expired",      label: "Vencido" },
  { value: "closed",       label: "Cerrado" },
];

const TOOLBOX_TEMAS_OPTIONS = [
  "Alcance y objetivo del trabajo",
  "Riesgos críticos identificados",
  "Medidas de control y EPP",
  "Rol y responsabilidad de cada persona",
  "Plan de emergencia y evacuación",
  "Punto de reunión",
  "Comunicación entre equipo",
  "Stop Work Authority",
  "Reportes de cuasi-accidente",
  "Lecciones aprendidas previas",
];

const TOOLBOX_TEMAS_I18N = {
  "Alcance y objetivo del trabajo":       "wp_topic_scope",
  "Riesgos críticos identificados":       "wp_topic_critical_risks",
  "Medidas de control y EPP":             "wp_topic_controls_ppe",
  "Rol y responsabilidad de cada persona":"wp_topic_roles",
  "Plan de emergencia y evacuación":      "wp_topic_emergency",
  "Punto de reunión":                     "wp_topic_meeting_point",
  "Comunicación entre equipo":            "wp_topic_communication",
  "Stop Work Authority":                  "wp_topic_swa",
  "Reportes de cuasi-accidente":          "wp_topic_near_miss",
  "Lecciones aprendidas previas":         "wp_topic_lessons",
};

const VINCULO_OPTIONS = [
  { value: "poe",              label: "Procedimiento Operativo Estándar (POE)" },
  { value: "instructivo",      label: "Instructivo de trabajo" },
  { value: "manual",           label: "Manual de seguridad" },
  { value: "permiso_anterior", label: "Permiso de trabajo anterior" },
  { value: "plan_sst",         label: "Plan de SST" },
  { value: "sin_vinculo",      label: "Sin vínculo documental" },
];

const CATEGORIA_LEGACY = {
  "mecanico": "mechanical", "fisico": "physical", "quimico": "chemical",
  "electrico": "electrical", "energia_almacenada": "stored_energy",
  "caida_altura": "fall_height", "caida_objetos": "falling_objects",
  "atmosfera": "hazardous_atmosphere", "termico": "thermal",
  "biologico": "biological", "ergonomico": "ergonomic",
  "psicosocial": "psychosocial", "ambiental": "environmental",
  "fuego_explosion": "fire_explosion", "externo": "external_hazard",
};

const CATEGORIA_OPTIONS = [
  { value: "mechanical"           },
  { value: "physical"             },
  { value: "chemical"             },
  { value: "electrical"           },
  { value: "stored_energy"        },
  { value: "fall_height"          },
  { value: "falling_objects"      },
  { value: "hazardous_atmosphere" },
  { value: "thermal"              },
  { value: "biological"           },
  { value: "ergonomic"            },
  { value: "psychosocial"         },
  { value: "environmental"        },
  { value: "fire_explosion"       },
  { value: "external_hazard"      },
];

const JERARQUIA_LEGACY = {
  "eliminacion": "elimination", "sustitucion": "substitution",
  "ingenieria": "engineering_controls", "administrativo": "administrative_controls",
  "epp": "ppe",
};

const JERARQUIA_OPTIONS = [
  { value: "elimination"            },
  { value: "substitution"           },
  { value: "engineering_controls"   },
  { value: "administrative_controls"},
  { value: "ppe"                    },
];

// Ref.: Shell HSSE & SP Control Framework — Last Minute Risk Assessment
const LMRA_PREGUNTAS = [
  "¿Todo el personal cuenta con la capacitación adecuada para realizar esta tarea?",
  "¿Las condiciones del sitio coinciden con lo descrito en el permiso de trabajo?",
  "¿Los riesgos han sido identificados y son comprendidos por todo el equipo?",
  "¿EPP, herramientas y equipos están disponibles, verificados y en buen estado?",
];

const CRITERIO_OPTIONS = [
  { value: "auto",      label: "Auto — creador del permiso" },
  { value: "usuario",   label: "Usuario específico" },
  { value: "rol",       label: "Por rol" },
  { value: "jerarquia", label: "Jerarquía (jefe inmediato del sector)" },
];

const ROL_OPCIONES = [
  "Supervisor del sector",
  "Jefe de Mantenimiento",
  "Jefe de Operaciones",
  "Responsable EHS/SST",
  "Gerente del Sitio",
  "Director de Operaciones",
];

const ACCIONES_OPTIONS = [
  { value: "aprobar",             label: "Aprobar" },
  { value: "aprobar_condiciones", label: "Aprobar con condiciones" },
  { value: "rechazar",            label: "Rechazar" },
  { value: "suspender",           label: "Suspender" },
  { value: "solicitar_cambios",   label: "Solicitar cambios" },
];

const DEFAULT_FLUJO_LEVELS = [
  {
    nivel: "Nivel 1 — Solicitante",
    rol: "Creador del permiso",
    criterio: "auto",
    titular: "",
    suplentes: [],
    acciones: ["aprobar"],
    activo: true,
    firmaDigital: true,
    comentarioObligatorio: true,
    requeridoPorTipo: false,
  },
  {
    nivel: "Nivel 2 — Supervisor del área",
    rol: "Supervisor del área operativa",
    criterio: "rol",
    titular: "Supervisor del sector",
    suplentes: [],
    acciones: ["aprobar", "aprobar_condiciones", "rechazar", "suspender", "solicitar_cambios"],
    activo: true,
    firmaDigital: true,
    comentarioObligatorio: true,
    requeridoPorTipo: false,
  },
  {
    nivel: "Nivel 3 — Responsable EHS/SST",
    rol: "Aprobador de seguridad",
    criterio: "rol",
    titular: "Responsable EHS/SST",
    suplentes: [],
    acciones: ["aprobar", "aprobar_condiciones", "rechazar", "suspender", "solicitar_cambios"],
    activo: true,
    firmaDigital: true,
    comentarioObligatorio: true,
    requeridoPorTipo: false,
  },
  {
    nivel: "Nivel 4 — Verificación in-situ",
    rol: "Verificador en campo",
    criterio: "usuario",
    titular: "",
    suplentes: [],
    acciones: ["aprobar", "rechazar", "suspender"],
    activo: true,
    firmaDigital: true,
    comentarioObligatorio: true,
    requeridoPorTipo: false,
  },
  {
    nivel: "Nivel 5 — Cierre",
    rol: "Responsable del cierre formal",
    criterio: "rol",
    titular: "Responsable EHS/SST",
    suplentes: [],
    acciones: ["aprobar", "solicitar_cambios"],
    activo: true,
    firmaDigital: true,
    comentarioObligatorio: true,
    requeridoPorTipo: false,
  },
];

const ESCALAR_A_LABELS = {
  jefe_directo:  "Jefe directo del aprobador",
  gerente_sitio: "Gerente del sitio",
  ehs_corporate: "EHS Corporate",
};

/**
 * Convierte los niveles del ApprovalConfig del sitio al formato flujoAprobacion del permiso.
 * - criterio "rol"/"auto"/"jerarquia": titular vacío (se resuelve dinámicamente al aprobar)
 * - criterio "usuario": copia userId de titulares[0] y suplentes
 */
function mapConfigToFlujo(levels) {
  return levels
    .filter(lv => lv.activo)
    .sort((a, b) => a.nivel - b.nivel)
    .map(lv => ({
      nivel:                lv.nombre,
      rol:                  lv.rol || lv.nombre,
      criterio:             lv.criterio,
      titular: lv.criterio === "usuario" && lv.titulares?.[0]?.userId
        ? lv.titulares[0].userId
        : "",
      suplentes: lv.criterio === "usuario"
        ? (lv.suplentes || []).map(s => s.userId).filter(Boolean)
        : [],
      acciones:             lv.acciones?.length ? lv.acciones : ["aprobar"],
      activo:               lv.activo ?? true,
      firmaDigital:         lv.firmaDigitalObligatoria ?? false,
      comentarioObligatorio: lv.comentarioObligatorio ?? false,
      requeridoPorTipo:     false,
    }));
}

// NOM-115-STPS · IRAM 3620 · ANSI Z89.1 · NR-6 · NCh 461
const EPP_CATALOG = {
  "Cabeza": [
    "Casco impacto clase G",
    "Casco dieléctrico clase E",
    "Casco conductivo clase C",
    "Barbiquejo",
    "Cofia",
    "Capucha contra impacto/calor",
  ],
  // NMX-S-057-SCFI · IRAM 3630 · ANSI Z87.1 · NR-6
  "Ojos y cara": [
    "Anteojos de seguridad",
    "Goggles antiimpacto",
    "Goggles químicos ventilación indirecta",
    "Pantalla facial",
    "Careta soldador con filtro DIN",
    "Gafas auxiliar oxicorte",
    "Pantalla auto-oscurecimiento",
  ],
  // NMX-S-035 · IRAM 4060 · ANSI S3.19 · NR-6
  "Oídos": [
    "Tapones desechables",
    "Tapones reutilizables silicona",
    "Conchas / orejeras",
    "Protección doble (tapón + orejera)",
    "Tapones con comunicación electrónica",
  ],
  // NOM-116-STPS-2009 · IRAM 3637/3646 · NIOSH 42 CFR Parte 84
  "Aparato respiratorio": [
    "Mascarilla desechable N95 / FFP2",
    "Mascarilla desechable N99 / FFP3",
    "Respirador media cara — P100 (partículas)",
    "Respirador media cara — filtros químicos (OV/AG)",
    "Respirador media cara — filtros combinados (P100 + OV/AG)",
    "Respirador cara completa — filtros químicos",
    "Respirador cara completa — filtros combinados",
    "Respirador motorizado PAPR",
    "Aire suministrado SAR (línea de aire)",
    "Autónomo SCBA circuito abierto",
    "Autónomo SCBA circuito cerrado",
    "Mascarilla quirúrgica (riesgo biológico)",
    "SCBA de escape (30 min)",
  ],
  // IRAM 3859 · NOM-113-STPS · EN 388 · EN 374 · EN 407
  "Manos": [
    "Guantes de nitrilo",
    "Guantes de látex",
    "Guantes de vaqueta/cuero",
    "Guantes anticorte nivel 5 (EN 388)",
    "Guantes anticorte cota mallada",
    "Guantes térmicos / anticaloría",
    "Guantes dieléctricos clase 0 (500 V)",
    "Guantes dieléctricos clase 2 (17 000 V)",
    "Guantes dieléctricos clase 4 (36 000 V)",
    "Guantes soldador cuero",
    "Guantes anti-vibración",
    "Guantes descartables (polietileno)",
  ],
  // NOM-113-STPS · IRAM 3610 · ANSI Z41 · EN ISO 20345
  "Pies": [
    "Botín seguridad puntera acero",
    "Calzado dieléctrico",
    "Calzado antiestático",
    "Calzado anti-perforante",
    "Bota caña alta",
    "Polainas térmicas",
    "Cubre-calzado químico",
    "Botas PVC altas",
  ],
  // ANSI 107 (alta visibilidad) · NFPA 70E (arco eléctrico)
  "Cuerpo / Tronco": [
    "Mameluco descartable Tyvek",
    "Mameluco químico (Saran/Tyvek F)",
    "Traje HazMat encapsulado",
    "Chaleco alta visibilidad",
    "Ropa ignífuga (FR)",
    "Traje de arco eléctrico (cal/cm²)",
    "Delantal soldador",
    "Delantal químico",
    "Traje impermeable",
    "Chaleco refrigerante",
  ],
  // ANSI Z359 · EN 361/355/353 · IRAM 3605 · NOM-009-STPS
  "Anti-caídas (altura)": [
    "Arnés paracaidista 5 puntos",
    "Eslinga con absorbedor de energía",
    "Eslinga doble Y-lanyard",
    "Línea de vida horizontal",
    "Retráctil / SRL",
    "Anclaje certificado",
    "Mosquetones doble seguro",
    "Sistema de rescate",
  ],
  "Otros": [
    "Chaleco salvavidas",
    "Traje impermeable (exterior)",
    "Rodilleras",
    "Codera",
    "Faja lumbar (con prescripción médica)",
    "Salvavidas con luz",
    "Protector solar FPS 50+",
    "Repelente de insectos",
    "Mosquetero",
    "Dosímetro de radiación",
    "GPS personal",
  ],
};

const EPP_REGION_META = {
  "Cabeza":               { icon: "🪖", normativas: "NOM-115-STPS-2009 (MEX) · IRAM 3620 (ARG) · ANSI Z89.1" },
  "Ojos y cara":          { icon: "👁️",  normativas: "NMX-S-057/056-SCFI · IRAM 3630 · ANSI Z87.1" },
  "Oídos":                { icon: "👂", normativas: "NMX-S-035 · IRAM 4060 · ANSI S3.19" },
  "Aparato respiratorio": { icon: "😷", normativas: "NOM-116-STPS-2009 · IRAM 3637/3646 · NIOSH 42 CFR P.84" },
  "Manos":                { icon: "🧤", normativas: "IRAM 3859 · NOM-113-STPS · EN 388 · EN 374 · EN 407" },
  "Pies":                 { icon: "👢", normativas: "NOM-113-STPS · IRAM 3610 · ANSI Z41 · EN ISO 20345" },
  "Cuerpo / Tronco":      { icon: "🦺", normativas: "ANSI 107 (alta vis.) · NFPA 70E (arco eléctrico)" },
  "Anti-caídas (altura)": { icon: "🪝", normativas: "ANSI Z359 · EN 361/355/353 · IRAM 3605 · NOM-009-STPS" },
  "Otros":                { icon: "🔧", normativas: "" },
};

const EPP_ITEM_DESC = {
  "Casco impacto clase G":                  "Impacto y penetración de objetos; voltaje hasta 2.200 V",
  "Casco dieléctrico clase E":              "Trabajos eléctricos; aislante hasta 20.000 V",
  "Casco conductivo clase C":               "Solo impacto, sin riesgo eléctrico",
  "Barbiquejo":                             "Altura, viento, espacios donde el casco pueda caer",
  "Cofia":                                  "Industria alimenticia, farma, hospitales — contención del cabello",
  "Capucha contra impacto/calor":           "Exposición a partículas calientes o frío",
  "Anteojos de seguridad":                  "Impacto, polvo, partículas voladoras",
  "Goggles antiimpacto":                    "Mayor sellado; polvo, partículas, viento",
  "Goggles químicos ventilación indirecta": "Salpicaduras de líquidos químicos",
  "Pantalla facial":                        "Salpicadura, esmerilado, contención de químicos",
  "Careta soldador con filtro DIN":         "Soldadura — radiación UV/IR según proceso",
  "Gafas auxiliar oxicorte":               "Oxicorte, brazing; bajo intensidad",
  "Pantalla auto-oscurecimiento":           "Soldadura prolongada con varios procesos",
  "Tapones desechables":                    "Ruido continuo; NRR 29 dB",
  "Tapones reutilizables silicona":         "Comodidad en uso prolongado",
  "Conchas / orejeras":                     "Protección adicional a tapones",
  "Protección doble (tapón + orejera)":     "Ruido extremo > 100 dB",
  "Tapones con comunicación electrónica":   "Comunicación en entornos ruidosos",
  "Mascarilla desechable N95 / FFP2":       "Polvo, aerosoles, partículas < 0,3 µm; 94% filtración",
  "Mascarilla desechable N99 / FFP3":       "Alta filtración; TB, metales pesados",
  "Respirador media cara — P100 (partículas)": "Partículas > 99,97%; polvo, humos metálicos",
  "Respirador media cara — filtros químicos (OV/AG)": "Vapores orgánicos, ácidos",
  "Respirador media cara — filtros combinados (P100 + OV/AG)": "Combinado partículas + vapores",
  "Respirador cara completa — filtros químicos": "Protección ocular + respiratoria",
  "Respirador cara completa — filtros combinados": "Protección total partículas + vapores",
  "Respirador motorizado PAPR":             "Presión positiva; grandes volúmenes de trabajo",
  "Aire suministrado SAR (línea de aire)": "Espacios confinados; concentraciones elevadas",
  "Autónomo SCBA circuito abierto":        "Rescate e intervención de emergencia",
  "Autónomo SCBA circuito cerrado":        "Minas, túneles; sin fuente de aire",
  "Mascarilla quirúrgica (riesgo biológico)": "Salpicaduras biológicas; no protege aerosoles",
  "SCBA de escape (30 min)":               "Escape en emergencia — 30 min de autonomía",
  "Guantes de nitrilo":                    "Químicos, aceites, cortes leves",
  "Guantes de látex":                      "Riesgo biológico, laboratorio",
  "Guantes de vaqueta/cuero":              "Trabajos manuales, construcción",
  "Guantes anticorte nivel 5 (EN 388)":   "Vidrio, metales, bordes filosos",
  "Guantes anticorte cota mallada":        "Carnicería, industria alimentaria",
  "Guantes térmicos / anticaloría":        "Calor, frío extremo, fundición",
  "Guantes dieléctricos clase 0 (500 V)":  "Baja tensión hasta 500 V",
  "Guantes dieléctricos clase 2 (17 000 V)": "Media tensión",
  "Guantes dieléctricos clase 4 (36 000 V)": "Alta tensión",
  "Guantes soldador cuero":                "Soldadura, temperatura alta",
  "Guantes anti-vibración":                "Herramientas vibrantes; síndrome mano-brazo",
  "Guantes descartables (polietileno)":    "Contaminantes leves, alimentación",
  "Botín seguridad puntera acero":         "Aplastamiento, impacto de objetos",
  "Calzado dieléctrico":                   "Sin conexión a tierra; riesgo eléctrico",
  "Calzado antiestático":                  "Disipación de carga estática",
  "Calzado anti-perforante":               "Clavos, vidrios, objetos punzantes",
  "Bota caña alta":                        "Barro, agua, terrenos irregulares",
  "Polainas térmicas":                     "Soldadura, fundición, chispas",
  "Cubre-calzado químico":                 "Salpicaduras químicas",
  "Botas PVC altas":                       "Líquidos, barro, productos agroquímicos",
  "Mameluco descartable Tyvek":            "Partículas finas, polvo, salpicaduras leves",
  "Mameluco químico (Saran/Tyvek F)":      "Químicos líquidos y aerosoles",
  "Traje HazMat encapsulado":              "Sustancias peligrosas — Nivel A/B",
  "Chaleco alta visibilidad":              "Tráfico vehicular, zonas de baja visibilidad",
  "Ropa ignífuga (FR)":                    "Llamas repentinas, fuego flash",
  "Traje de arco eléctrico (cal/cm²)":    "Protección térmica por arco eléctrico",
  "Delantal soldador":                     "Chispas, salpicaduras metálicas",
  "Delantal químico":                      "Salpicaduras de ácidos o bases",
  "Traje impermeable":                     "Lluvia intensa, trabajo en mojado",
  "Chaleco refrigerante":                  "Trabajo en calor extremo",
  "Arnés paracaidista 5 puntos":           "Trabajo en altura > 1,8 m",
  "Eslinga con absorbedor de energía":     "Caída libre; reduce fuerzas de impacto",
  "Eslinga doble Y-lanyard":               "100% anclado al desplazarse",
  "Línea de vida horizontal":              "Desplazamiento en techos o estructuras",
  "Retráctil / SRL":                       "Libertad de movimiento; detención rápida",
  "Anclaje certificado":                   "Punto fijo certificado ≥ 22 kN",
  "Mosquetones doble seguro":              "Conexión segura arnés-línea de vida",
  "Sistema de rescate":                    "Evacuación vertical en emergencias",
  "Chaleco salvavidas":                    "Trabajo sobre o cerca del agua",
  "Traje impermeable (exterior)":          "Lluvia, viento, trabajo en intemperie",
  "Rodilleras":                            "Trabajo en superficies duras de rodillas",
  "Codera":                                "Protección de codos en trabajo manual",
  "Faja lumbar (con prescripción médica)": "Manejo de cargas; solo con indicación médica",
  "Salvavidas con luz":                    "Trabajo nocturno sobre el agua",
  "Protector solar FPS 50+":              "Trabajo a la intemperie; radiación UV",
  "Repelente de insectos":                 "Zonas con vectores (dengue, malaria)",
  "Mosquetero":                            "Pernocte en campo; protección vectores",
  "Dosímetro de radiación":                "Zonas con radiación ionizante",
  "GPS personal":                          "Trabajo en zonas remotas o aisladas",
};

const EPP_REGION_I18N = {
  "Cabeza":               "epp_r_cabeza",
  "Ojos y cara":          "epp_r_ojos_cara",
  "Oídos":                "epp_r_oidos",
  "Aparato respiratorio": "epp_r_respiratorio",
  "Manos":                "epp_r_manos",
  "Pies":                 "epp_r_pies",
  "Cuerpo / Tronco":      "epp_r_cuerpo_tronco",
  "Anti-caídas (altura)": "epp_r_anticaidas",
  "Otros":                "epp_r_otros",
};

const EPP_ITEM_I18N = {
  "Casco impacto clase G":                  { n: "epp_i_casco_g",      d: "epp_d_casco_g" },
  "Casco dieléctrico clase E":              { n: "epp_i_casco_e",      d: "epp_d_casco_e" },
  "Casco conductivo clase C":               { n: "epp_i_casco_c",      d: "epp_d_casco_c" },
  "Barbiquejo":                             { n: "epp_i_barbiquejo",   d: "epp_d_barbiquejo" },
  "Cofia":                                  { n: "epp_i_cofia",        d: "epp_d_cofia" },
  "Capucha contra impacto/calor":           { n: "epp_i_capucha",      d: "epp_d_capucha" },
  "Anteojos de seguridad":                  { n: "epp_i_anteojos",     d: "epp_d_anteojos" },
  "Goggles antiimpacto":                    { n: "epp_i_goggles_a",    d: "epp_d_goggles_a" },
  "Goggles químicos ventilación indirecta": { n: "epp_i_goggles_q",    d: "epp_d_goggles_q" },
  "Pantalla facial":                        { n: "epp_i_pantalla_f",   d: "epp_d_pantalla_f" },
  "Careta soldador con filtro DIN":         { n: "epp_i_careta_din",   d: "epp_d_careta_din" },
  "Gafas auxiliar oxicorte":               { n: "epp_i_gafas_oxy",    d: "epp_d_gafas_oxy" },
  "Pantalla auto-oscurecimiento":           { n: "epp_i_pantalla_ao",  d: "epp_d_pantalla_ao" },
  "Tapones desechables":                    { n: "epp_i_tap_d",        d: "epp_d_tap_d" },
  "Tapones reutilizables silicona":         { n: "epp_i_tap_s",        d: "epp_d_tap_s" },
  "Conchas / orejeras":                     { n: "epp_i_conchas",      d: "epp_d_conchas" },
  "Protección doble (tapón + orejera)":     { n: "epp_i_prot_doble",   d: "epp_d_prot_doble" },
  "Tapones con comunicación electrónica":   { n: "epp_i_tap_com",      d: "epp_d_tap_com" },
  "Mascarilla desechable N95 / FFP2":       { n: "epp_i_n95",          d: "epp_d_n95" },
  "Mascarilla desechable N99 / FFP3":       { n: "epp_i_n99",          d: "epp_d_n99" },
  "Respirador media cara — P100 (partículas)": { n: "epp_i_resp_mc_p100", d: "epp_d_resp_mc_p100" },
  "Respirador media cara — filtros químicos (OV/AG)": { n: "epp_i_resp_mc_ov", d: "epp_d_resp_mc_ov" },
  "Respirador media cara — filtros combinados (P100 + OV/AG)": { n: "epp_i_resp_mc_cb", d: "epp_d_resp_mc_cb" },
  "Respirador cara completa — filtros químicos": { n: "epp_i_resp_cc_q", d: "epp_d_resp_cc_q" },
  "Respirador cara completa — filtros combinados": { n: "epp_i_resp_cc_cb", d: "epp_d_resp_cc_cb" },
  "Respirador motorizado PAPR":             { n: "epp_i_papr",         d: "epp_d_papr" },
  "Aire suministrado SAR (línea de aire)": { n: "epp_i_sar",          d: "epp_d_sar" },
  "Autónomo SCBA circuito abierto":        { n: "epp_i_scba_ab",      d: "epp_d_scba_ab" },
  "Autónomo SCBA circuito cerrado":        { n: "epp_i_scba_ce",      d: "epp_d_scba_ce" },
  "Mascarilla quirúrgica (riesgo biológico)": { n: "epp_i_mask_q",    d: "epp_d_mask_q" },
  "SCBA de escape (30 min)":               { n: "epp_i_scba_esc",     d: "epp_d_scba_esc" },
  "Guantes de nitrilo":                    { n: "epp_i_g_nit",        d: "epp_d_g_nit" },
  "Guantes de látex":                      { n: "epp_i_g_lat",        d: "epp_d_g_lat" },
  "Guantes de vaqueta/cuero":              { n: "epp_i_g_cuero",      d: "epp_d_g_cuero" },
  "Guantes anticorte nivel 5 (EN 388)":   { n: "epp_i_g_corte5",     d: "epp_d_g_corte5" },
  "Guantes anticorte cota mallada":        { n: "epp_i_g_malla",      d: "epp_d_g_malla" },
  "Guantes térmicos / anticaloría":        { n: "epp_i_g_term",       d: "epp_d_g_term" },
  "Guantes dieléctricos clase 0 (500 V)":  { n: "epp_i_g_diel0",     d: "epp_d_g_diel0" },
  "Guantes dieléctricos clase 2 (17 000 V)": { n: "epp_i_g_diel2",   d: "epp_d_g_diel2" },
  "Guantes dieléctricos clase 4 (36 000 V)": { n: "epp_i_g_diel4",   d: "epp_d_g_diel4" },
  "Guantes soldador cuero":                { n: "epp_i_g_sold",       d: "epp_d_g_sold" },
  "Guantes anti-vibración":                { n: "epp_i_g_vib",        d: "epp_d_g_vib" },
  "Guantes descartables (polietileno)":    { n: "epp_i_g_desc",       d: "epp_d_g_desc" },
  "Botín seguridad puntera acero":         { n: "epp_i_botin",        d: "epp_d_botin" },
  "Calzado dieléctrico":                   { n: "epp_i_calz_diel",    d: "epp_d_calz_diel" },
  "Calzado antiestático":                  { n: "epp_i_calz_est",     d: "epp_d_calz_est" },
  "Calzado anti-perforante":               { n: "epp_i_calz_perf",    d: "epp_d_calz_perf" },
  "Bota caña alta":                        { n: "epp_i_bota_alta",    d: "epp_d_bota_alta" },
  "Polainas térmicas":                     { n: "epp_i_polainas",     d: "epp_d_polainas" },
  "Cubre-calzado químico":                 { n: "epp_i_cubre",        d: "epp_d_cubre" },
  "Botas PVC altas":                       { n: "epp_i_botas_pvc",    d: "epp_d_botas_pvc" },
  "Mameluco descartable Tyvek":            { n: "epp_i_tyvek_d",      d: "epp_d_tyvek_d" },
  "Mameluco químico (Saran/Tyvek F)":      { n: "epp_i_tyvek_q",      d: "epp_d_tyvek_q" },
  "Traje HazMat encapsulado":              { n: "epp_i_hazmat",       d: "epp_d_hazmat" },
  "Chaleco alta visibilidad":              { n: "epp_i_vis_vest",      d: "epp_d_vis_vest" },
  "Ropa ignífuga (FR)":                    { n: "epp_i_fr",            d: "epp_d_fr" },
  "Traje de arco eléctrico (cal/cm²)":    { n: "epp_i_arco",          d: "epp_d_arco" },
  "Delantal soldador":                     { n: "epp_i_del_sold",      d: "epp_d_del_sold" },
  "Delantal químico":                      { n: "epp_i_del_quim",      d: "epp_d_del_quim" },
  "Traje impermeable":                     { n: "epp_i_imper",         d: "epp_d_imper" },
  "Chaleco refrigerante":                  { n: "epp_i_refrig",        d: "epp_d_refrig" },
  "Arnés paracaidista 5 puntos":           { n: "epp_i_arnes",         d: "epp_d_arnes" },
  "Eslinga con absorbedor de energía":     { n: "epp_i_eslinga_ab",    d: "epp_d_eslinga_ab" },
  "Eslinga doble Y-lanyard":               { n: "epp_i_eslinga_y",     d: "epp_d_eslinga_y" },
  "Línea de vida horizontal":              { n: "epp_i_linea_h",       d: "epp_d_linea_h" },
  "Retráctil / SRL":                       { n: "epp_i_retractil",     d: "epp_d_retractil" },
  "Anclaje certificado":                   { n: "epp_i_anclaje",       d: "epp_d_anclaje" },
  "Mosquetones doble seguro":              { n: "epp_i_mosqueton",     d: "epp_d_mosqueton" },
  "Sistema de rescate":                    { n: "epp_i_rescate",       d: "epp_d_rescate" },
  "Chaleco salvavidas":                    { n: "epp_i_salv",          d: "epp_d_salv" },
  "Traje impermeable (exterior)":          { n: "epp_i_imper_ext",     d: "epp_d_imper_ext" },
  "Rodilleras":                            { n: "epp_i_rodilleras",    d: "epp_d_rodilleras" },
  "Codera":                                { n: "epp_i_coderas",       d: "epp_d_coderas" },
  "Faja lumbar (con prescripción médica)": { n: "epp_i_faja",          d: "epp_d_faja" },
  "Salvavidas con luz":                    { n: "epp_i_salv_luz",      d: "epp_d_salv_luz" },
  "Protector solar FPS 50+":              { n: "epp_i_solar",          d: "epp_d_solar" },
  "Repelente de insectos":                 { n: "epp_i_repelente",     d: "epp_d_repelente" },
  "Mosquetero":                            { n: "epp_i_mosquetero",    d: "epp_d_mosquetero" },
  "Dosímetro de radiación":                { n: "epp_i_dosimetro",     d: "epp_d_dosimetro" },
  "GPS personal":                          { n: "epp_i_gps",           d: "epp_d_gps" },
};

const EPP_SUGERIDO_POR_TIPO = {
  altura: [
    "Cabeza:Casco impacto clase G",
    "Anti-caídas (altura):Arnés paracaidista 5 puntos",
    "Anti-caídas (altura):Eslinga con absorbedor de energía",
    "Anti-caídas (altura):Eslinga doble Y-lanyard",
    "Anti-caídas (altura):Retráctil / SRL",
    "Anti-caídas (altura):Mosquetones doble seguro",
    "Pies:Botín seguridad puntera acero",
  ],
  caliente: [
    "Ojos y cara:Careta soldador con filtro DIN",
    "Ojos y cara:Pantalla auto-oscurecimiento",
    "Ojos y cara:Gafas auxiliar oxicorte",
    "Manos:Guantes térmicos / anticaloría",
    "Manos:Guantes soldador cuero",
    "Cuerpo / Tronco:Ropa ignífuga (FR)",
    "Cuerpo / Tronco:Delantal soldador",
    "Pies:Polainas térmicas",
  ],
  espacio_confinado: [
    "Aparato respiratorio:Autónomo SCBA circuito abierto",
    "Aparato respiratorio:Aire suministrado SAR (línea de aire)",
    "Aparato respiratorio:Respirador cara completa — filtros combinados",
    "Anti-caídas (altura):Arnés paracaidista 5 puntos",
    "Anti-caídas (altura):Sistema de rescate",
    "Ojos y cara:Goggles químicos ventilación indirecta",
  ],
  electrico_loto: [
    "Cabeza:Casco dieléctrico clase E",
    "Manos:Guantes dieléctricos clase 0 (500 V)",
    "Manos:Guantes dieléctricos clase 2 (17 000 V)",
    "Pies:Calzado dieléctrico",
    "Ojos y cara:Pantalla facial",
    "Cuerpo / Tronco:Traje de arco eléctrico (cal/cm²)",
  ],
  izaje: [
    "Cabeza:Casco impacto clase G",
    "Manos:Guantes anticorte nivel 5 (EN 388)",
    "Pies:Botín seguridad puntera acero",
    "Cuerpo / Tronco:Chaleco alta visibilidad",
  ],
  excavacion: [
    "Cabeza:Casco impacto clase G",
    "Pies:Botín seguridad puntera acero",
    "Pies:Calzado anti-perforante",
    "Cuerpo / Tronco:Chaleco alta visibilidad",
    "Manos:Guantes de vaqueta/cuero",
  ],
  quimicos: [
    "Ojos y cara:Goggles químicos ventilación indirecta",
    "Ojos y cara:Pantalla facial",
    "Aparato respiratorio:Respirador media cara — filtros químicos (OV/AG)",
    "Aparato respiratorio:Respirador media cara — filtros combinados (P100 + OV/AG)",
    "Manos:Guantes de nitrilo",
    "Cuerpo / Tronco:Mameluco descartable Tyvek",
    "Cuerpo / Tronco:Delantal químico",
    "Cuerpo / Tronco:Mameluco químico (Saran/Tyvek F)",
  ],
};

const EPP_SUGERIDO_POR_CATEGORIA = {
  caida_altura: [
    "Anti-caídas (altura):Arnés paracaidista 5 puntos",
    "Anti-caídas (altura):Eslinga con absorbedor de energía",
    "Anti-caídas (altura):Eslinga doble Y-lanyard",
    "Anti-caídas (altura):Retráctil / SRL",
    "Cabeza:Casco impacto clase G",
  ],
  electrico: [
    "Manos:Guantes dieléctricos clase 0 (500 V)",
    "Manos:Guantes dieléctricos clase 2 (17 000 V)",
    "Pies:Calzado dieléctrico",
    "Cabeza:Casco dieléctrico clase E",
    "Cuerpo / Tronco:Traje de arco eléctrico (cal/cm²)",
  ],
  energia_almacenada: [
    "Manos:Guantes dieléctricos clase 0 (500 V)",
    "Ojos y cara:Pantalla facial",
    "Pies:Calzado antiestático",
  ],
  quimico: [
    "Ojos y cara:Goggles químicos ventilación indirecta",
    "Aparato respiratorio:Respirador media cara — filtros químicos (OV/AG)",
    "Manos:Guantes de nitrilo",
    "Cuerpo / Tronco:Mameluco descartable Tyvek",
  ],
  termico: [
    "Manos:Guantes térmicos / anticaloría",
    "Cuerpo / Tronco:Ropa ignífuga (FR)",
    "Ojos y cara:Careta soldador con filtro DIN",
  ],
  atmosfera: [
    "Aparato respiratorio:Autónomo SCBA circuito abierto",
    "Aparato respiratorio:Aire suministrado SAR (línea de aire)",
    "Aparato respiratorio:Respirador cara completa — filtros combinados",
  ],
  fuego_explosion: [
    "Cuerpo / Tronco:Ropa ignífuga (FR)",
    "Manos:Guantes térmicos / anticaloría",
    "Ojos y cara:Pantalla facial",
    "Ojos y cara:Careta soldador con filtro DIN",
  ],
  mecanico: [
    "Manos:Guantes anticorte nivel 5 (EN 388)",
    "Ojos y cara:Anteojos de seguridad",
    "Pies:Botín seguridad puntera acero",
    "Cabeza:Casco impacto clase G",
  ],
  caida_objetos: [
    "Cabeza:Casco impacto clase G",
    "Pies:Botín seguridad puntera acero",
  ],
  fisico: [
    "Oídos:Tapones desechables",
    "Oídos:Conchas / orejeras",
    "Otros:Protector solar FPS 50+",
  ],
  biologico: [
    "Manos:Guantes de nitrilo",
    "Manos:Guantes de látex",
    "Aparato respiratorio:Mascarilla quirúrgica (riesgo biológico)",
    "Aparato respiratorio:Mascarilla desechable N95 / FFP2",
    "Cuerpo / Tronco:Mameluco descartable Tyvek",
  ],
  ergonomico: [
    "Otros:Faja lumbar (con prescripción médica)",
    "Otros:Rodilleras",
    "Otros:Codera",
    "Manos:Guantes anti-vibración",
  ],
};

// ── Bloques condicionales por tipo ───────────────────────────────────────────
const ALTURA_MIN_PAIS = {
  AR: 2.0, BO: 1.8, BR: 2.0, CL: 1.8, CO: 2.0, CR: 1.8, DO: 1.8,
  EC: 1.8, GT: 1.8, MX: 1.8, NI: 1.8, PA: 1.8, PE: 1.8, PY: 2.0,
  SV: 1.8, UY: 2.0, VE: 1.8,
};
const EXCAV_MIN_PAIS = {
  AR: 1.2, BO: 1.5, BR: 1.25, CL: 1.5, CO: 1.5, CR: 1.2, DO: 1.0,
  EC: 1.5, GT: 1.5, MX: 1.5, NI: 1.5, PA: 1.5, PE: 1.5, PY: 1.5,
  SV: 1.5, UY: 1.5, VE: 1.5,
};

const ALTURA_MEDIOS = [
  "Andamio tubular", "Andamio multidireccional", "Andamio rodante",
  "Escalera tipo tijera", "Escalera de mano",
  "Plataforma elevadora tipo tijera (MEWP)", "Plataforma elevadora articulada",
  "Plataforma colgante / sillín", "Cuerda de trabajo / acceso vertical",
];
const CALIENTE_SUBTIPOS = [
  "Soldadura SMAW / MIG / TIG", "Oxicorte", "Esmerilado con disco abrasivo",
  "Llama abierta (soplete, mechero)", "Pistola de calor",
  "Amolado / corte con ángulo", "Remachado en caliente",
];
const EC_CLASES = [
  "Clase A — Inmediatamente peligroso para la vida (IDLH)",
  "Clase B — Potencialmente peligroso",
  "Clase C — Sin riesgo atmosférico significativo",
];
const EC_FRECUENCIA_POR_CLASE = {
  "Clase A — Inmediatamente peligroso para la vida (IDLH)": "Continuo",
  "Clase B — Potencialmente peligroso": "Cada 30 min",
  "Clase C — Sin riesgo atmosférico significativo": "Cada 60 min",
};
const EL_TENSION_OPTIONS = [
  "BT — Baja tensión (< 1 kV)",
  "MT — Media tensión (1–35 kV)",
  "AT — Alta tensión (> 35 kV)",
];
const EL_EPP_POR_TENSION = {
  "BT — Baja tensión (< 1 kV)":   ["Guantes dieléctricos clase 0 (500 V)", "Calzado dieléctrico", "Casco dieléctrico clase E"],
  "MT — Media tensión (1–35 kV)": ["Guantes dieléctricos clase 2 (17 000 V)", "Traje de arco eléctrico (cal/cm²)", "Pantalla facial", "Calzado dieléctrico", "Casco dieléctrico clase E"],
  "AT — Alta tensión (> 35 kV)":  ["Guantes dieléctricos clase 4 (36 000 V)", "Traje de arco eléctrico (cal/cm²)", "Pantalla facial", "Calzado dieléctrico", "Casco dieléctrico clase E"],
};
const AC_TIPO_TECHO = [
  "Plano transitable", "Inclinado < 15°", "Inclinado 15–30°",
  "Inclinado > 30°", "Frágil / Translúcido",
];
const RD_TIPOS = [
  "No ionizante — UV / microondas / láser",
  "Ionizante — Rayos X / Gamma / Alfa / Beta",
  "Radiofrecuencia (RF)",
];

const BLOQUE_CONTROLES = {
  alt: [
    "Punto de anclaje certificado y verificado",
    "Arnés y línea de vida inspeccionados antes del ascenso",
    "Zona de exclusión delimitada bajo el área de trabajo",
    "Herramientas con guarda-caídas / cordino de seguridad",
    "Acceso y egreso seguros al área de trabajo",
    "Condiciones climáticas evaluadas (viento, lluvia, hielo)",
    "Personal sin afecciones que impidan trabajo en altura",
    "Procedimiento de rescate en altura definido y comunicado",
    "Comunicación activa con supervisor en tierra",
  ],
  cal: [
    "Extintor tipo ABC operativo y accesible (radio 10 m)",
    "Vigía de fuego (fire-watch) designado y en posición",
    "Área libre de combustibles / inflamables (radio 10 m)",
    "Líneas con gases o líquidos inflamables desconectadas o bloqueadas",
    "Autorización de interrupción de sistemas de detección / supresión",
    "Equipos de corte / soldadura revisados, sin pérdidas de gas",
    "Plan de emergencia contra incendio conocido por el equipo",
  ],
  ec: [
    "Ventilación mecánica activa antes y durante el ingreso",
    "Rescatador entrenado en posición con equipo de rescate completo",
    "Comunicación continua vigía ↔ trabajador confirmada",
    "Medición atmosférica OK en todos los puntos del espacio",
    "Equipos de respiración autónoma (SCBA) disponibles",
    "Aislamiento de energías peligrosas (LOTO) completado",
    "Registro de ingreso y egreso de personal activo",
    "Plan de emergencia y rescate específico definido",
    "Personal sin síntomas de intoxicación u otras afecciones",
    "Dimensiones del espacio verificadas para rescate con camilla",
  ],
  el: [
    "Energía eléctrica desenergizada, bloqueada y etiquetada",
    "Sistemas hidráulicos / neumáticos despresuri­zados y bloqueados",
    "Energía gravitacional asegurada (apuntalado, soportes)",
    "Energía química neutralizada y residuos contenidos",
    "Energía cinética / mecánica bloqueada (frenos, cuñas)",
    "Energía térmica disipada a temperatura ambiente",
  ],
  iz: [
    "Grúa / montacargas con certificación vigente y revisión pre-uso",
    "Rigger certificado presente y dirigiendo la maniobra",
    "Plan de izaje elaborado y aprobado por ingeniero",
    "Zona de exclusión delimitada (mínimo 1.5 × envergadura de carga)",
    "Señales de comunicación y canales de radio definidos",
    "Condiciones climáticas evaluadas (viento < 50 km/h)",
    "Estrobos, aparejos y grilletes verificados",
    "Centro de gravedad de la carga identificado y marcado",
  ],
  qu: [
    "FDS / SDS disponible en el sitio de trabajo",
    "Personal capacitado en manejo de la sustancia específica",
    "Recipientes etiquetados según SGA / GHS",
    "Kit de derrame disponible, accesible y en buen estado",
    "Zona de trabajo con ventilación suficiente",
    "Disposición final de residuos peligrosos definida",
    "Plan de emergencia por derrame / exposición disponible",
  ],
  ex: [
    "Relevamiento de servicios subterráneos (gas, agua, electricidad, telecom)",
    "Entibado o talud ejecutado según tipo de suelo y profundidad",
    "Acceso y egreso seguros (escalera, rampa certificada)",
    "Delimitación y señalización perimetral completa",
    "Estabilidad del suelo verificada antes y durante el ingreso",
    "Control de agua superficial y drenaje activo",
    "Plan de respuesta ante derrumbe o inundación definido",
    "Monitoreo de atmósfera en excavaciones profundas (> 1.5 m)",
  ],
  cd: [
    "Certificado de habilitación de la caldera vigente",
    "Operador de caldera certificado presente durante el trabajo",
    "Sistemas de seguridad (válvulas, manómetros) verificados",
    "Despresurización y enfriamiento previo al trabajo",
    "Plan de emergencia por fuga o explosión definido",
  ],
  dm: [
    "Plano de instalaciones y servicios disponible y revisado",
    "Corte de servicios (gas, electricidad, agua) verificado",
    "Estabilidad estructural evaluada por ingeniero",
    "Zona de exclusión delimitada con radio de seguridad",
    "Control de polvo, proyecciones y partículas",
    "Gestión de residuos y escombros según normativa",
  ],
  ac: [
    "Resistencia de la cubierta verificada para carga de personas y equipos",
    "Sistema anticaída instalado (línea de vida, anclajes certificados)",
    "Protección de bordes perimetrales (barandilla / malla de seguridad)",
    "Escalera de acceso fija o provisional asegurada",
    "Zona de exclusión delimitada en el perímetro inferior",
    "Condiciones climáticas evaluadas (viento, lluvia, hielo)",
  ],
  rd: [
    "Autorización de autoridad nuclear / ambiental vigente",
    "Dosímetros personales distribuidos y activos en todo el personal",
    "Zona controlada delimitada y señalizada (IAEA / normativa local)",
    "Personal con capacitación en protección radiológica acreditada",
    "Tiempo de exposición calculado y dentro de límites regulatorios",
    "Blindaje instalado y verificado según tipo y energía de la fuente",
    "Plan de emergencia radiológica disponible en sitio",
  ],
};

const initControles = (key) =>
  BLOQUE_CONTROLES[key].map(item => ({ item, estado: "" }));

const initTiposData = (td) => ({
  alt: {
    altura:    td?.alt?.altura    ?? "",
    medios:    td?.alt?.medios    || [],
    controles: td?.alt?.controles?.length ? td.alt.controles : initControles("alt"),
  },
  cal: {
    subtipos:  td?.cal?.subtipos  || [],
    controles: td?.cal?.controles?.length ? td.cal.controles : initControles("cal"),
  },
  ec: {
    identificacion: td?.ec?.identificacion || "",
    clase:          td?.ec?.clase          || "",
    equipo:         td?.ec?.equipo         || "",
    calibracion:    td?.ec?.calibracion    ? new Date(td.ec.calibracion).toISOString().slice(0, 10) : "",
    horaMed:        td?.ec?.horaMed        ? toDateTimeLocal(td.ec.horaMed) : "",
    o2:             td?.ec?.o2             ?? "",
    lel:            td?.ec?.lel            ?? "",
    h2s:            td?.ec?.h2s            ?? "",
    co:             td?.ec?.co             ?? "",
    frecuencia:     td?.ec?.frecuencia     || "",
    controles:      td?.ec?.controles?.length ? td.ec.controles : initControles("ec"),
  },
  el: {
    tension:   td?.el?.tension   || "",
    epp:       td?.el?.epp       || [],
    loto:      td?.el?.loto?.length ? td.el.loto : initControles("el"),
  },
  iz: {
    equipo:    td?.iz?.equipo    || "",
    capacidad: td?.iz?.capacidad ?? "",
    carga:     td?.iz?.carga     ?? "",
    controles: td?.iz?.controles?.length ? td.iz.controles : initControles("iz"),
  },
  qu: {
    sustancia:     td?.qu?.sustancia     || "",
    hojaSeguridad: td?.qu?.hojaSeguridad || "",
    controles:     td?.qu?.controles?.length ? td.qu.controles : initControles("qu"),
  },
  ex: {
    profundidad: td?.ex?.profundidad ?? "",
    controles:   td?.ex?.controles?.length ? td.ex.controles : initControles("ex"),
  },
  cd: {
    identificacion:  td?.cd?.identificacion  || "",
    presion:         td?.cd?.presion         ?? "",
    ingresaPersonal: td?.cd?.ingresaPersonal ?? false,
    controles:       td?.cd?.controles?.length ? td.cd.controles : initControles("cd"),
  },
  dm: {
    area:      td?.dm?.area      || "",
    controles: td?.dm?.controles?.length ? td.dm.controles : initControles("dm"),
  },
  ac: {
    tipoTecho: td?.ac?.tipoTecho || "",
    controles: td?.ac?.controles?.length ? td.ac.controles : initControles("ac"),
  },
  rd: {
    tipo:      td?.rd?.tipo      || "",
    fuente:    td?.rd?.fuente    || "",
    controles: td?.rd?.controles?.length ? td.rd.controles : initControles("rd"),
  },
});

const gasStatus = (field, val) => {
  if (val === "" || val === undefined || val === null) return null;
  const n = Number(val);
  if (isNaN(n)) return null;
  if (field === "o2")  return (n >= 19.5 && n <= 23.5) ? "ok" : (n < 18 || n > 25) ? "danger" : "warn";
  if (field === "lel") return n < 10 ? "ok" : n < 25 ? "warn" : "danger";
  if (field === "h2s") return n <= 10 ? "ok" : n <= 50 ? "warn" : "danger";
  if (field === "co")  return n <= 25 ? "ok" : n <= 200 ? "warn" : "danger";
  return null;
};
const GAS_LABELS  = { o2: "O₂ (%)", lel: "LEL (%)", h2s: "H₂S (ppm)", co: "CO (ppm)" };
const GAS_LIMITS  = {
  o2:  "Normal: 19.5–23.5 %",
  lel: "Normal: < 10 %",
  h2s: "Normal: ≤ 10 ppm",
  co:  "Normal: ≤ 25 ppm",
};

const EMPTY_PASO = {
  paso: "", categoria: "", peligro: "",
  sevIn: 1, probIn: 1,
  jerarquia: "", controles: "",
  sevRes: 1, probRes: 1,
  responsable: "",
};

const nivelRiesgo = (p, s) => {
  const n = (p || 1) * (s || 1);
  if (n <= 4)  return { n, label: "Bajo",     cls: "nr-low" };
  if (n <= 9)  return { n, label: "Mod.",     cls: "nr-mod" };
  if (n <= 14) return { n, label: "Alto",     cls: "nr-high" };
  return             { n, label: "Crítico",   cls: "nr-crit" };
};

const toDateTimeLocal = (val) => {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d) ? "" : d.toISOString().slice(0, 16);
};

const calcDuracion = (inicio, fin) => {
  if (!inicio || !fin) return null;
  const diff = new Date(fin) - new Date(inicio);
  if (diff <= 0) return null;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

function Section({ title, open, onToggle, children }) {
  return (
    <div className="wpf-section">
      <button type="button" className="wpf-section__header" onClick={onToggle}>
        <span className={`wpf-section__chevron${open ? " open" : ""}`}>▼</span>
        <span className="wpf-section__title">{title}</span>
      </button>
      {open && <div className="wpf-section__body">{children}</div>}
    </div>
  );
}

function FieldLabel({ children, required }) {
  return (
    <div className="wpf-label">
      {children}{required && <span className="wpf-req"> *</span>}
    </div>
  );
}

function TriToggle({ value, onChange }) {
  return (
    <div className="wpf-tri-toggle">
      <button type="button"
        className={`wpf-tri-btn${value === "bien" ? " wpf-tri-btn--bien" : ""}`}
        onClick={() => onChange(value === "bien" ? "" : "bien")}>B</button>
      <button type="button"
        className={`wpf-tri-btn${value === "mal" ? " wpf-tri-btn--mal" : ""}`}
        onClick={() => onChange(value === "mal" ? "" : "mal")}>M</button>
      <button type="button"
        className={`wpf-tri-btn${value === "na" ? " wpf-tri-btn--na" : ""}`}
        onClick={() => onChange(value === "na" ? "" : "na")}>N/A</button>
    </div>
  );
}


export function WorkPermitForm({ permit, close, onReload }) {
  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const canManageCatalogs = isMaster(user?.role) || isAdmin(user?.role);
  const { site: contextSite } = useSite();
  const { company: contextCompany } = useCompany();
  const hasContextSite    = contextSite    && contextSite    !== "0" && contextSite    !== 0;
  const hasContextCompany = contextCompany && contextCompany !== "0" && contextCompany !== 0;
  const [saving,          setSaving]          = useState(false);
  const [allSites,        setAllSites]        = useState([]);
  const [loadingSites,    setLoadingSites]    = useState(false);
  const [sectors,         setSectors]         = useState([]);
  const [loadingSectors,  setLoadingSectors]  = useState(false);
  const [showSiteModal,   setShowSiteModal]   = useState(false);
  const [showSectorModal, setShowSectorModal] = useState(false);
  const [areas,           setAreas]           = useState([]);
  const [users,           setUsers]           = useState([]);
  const [employees,       setEmployees]       = useState([]);
  const [atsFile,         setAtsFile]         = useState(null);
  const [fdsFile,         setFdsFile]         = useState(null);
  const [approvalCfg,     setApprovalCfg]     = useState(null);
  const [evrs,            setEvrs]            = useState([]);
  const [evrsLoading,     setEvrsLoading]     = useState(false);
  const [condicionesChanged, setCondicionesChanged] = useState(false);
  const [eppStock,        setEppStock]        = useState({});
  const [eppStockLoading, setEppStockLoading] = useState(false);

  const [openSections,   setOpenSections]   = useState({ sec1: true, sec2: true, sec3: true, sec4: true, sec5: true, sec6: true, sec7: true });
  const [suplenteDraft,  setSuplenteDraft]  = useState({});
  const toggleSection = (key) => setOpenSections(p => ({ ...p, [key]: !p[key] }));


  const [form, setForm] = useState({
    pais:          permit?.pais              || "",
    site:          permit?.site?._id         || permit?.site        || (!permit && hasContextSite ? contextSite : "") || "",
    sector:        permit?.sector?._id       || permit?.sector      || "",
    area:          permit?.area?._id         || permit?.area        || "",
    ubicacion:     permit?.ubicacion         || "",
    tipos:         permit?.tipos             || [],
    descripcion:   permit?.descripcion       || "",
    ejecutora:     permit?.ejecutora         || "",
    startDateTime:        toDateTimeLocal(permit?.startDateTime),
    estimatedEndDateTime: toDateTimeLocal(permit?.estimatedEndDateTime),
    workExecutor:  permit?.workExecutor      || "",
    ehsSupervisor: permit?.ehsSupervisor?._id || permit?.ehsSupervisor || "",
    status:        permit?.status            || "draft",
    contratista: {
      nombre:          permit?.contratista?.nombre          || "",
      doc:             permit?.contratista?.doc             || "",
      supervisor:      permit?.contratista?.supervisor      || "",
      docs:            permit?.contratista?.docs            || [],
      docsVencimiento: permit?.contratista?.docsVencimiento || {},
    },
    personal: permit?.personal?.length
      ? permit.personal.map(p => ({
          employee:       p.employee?._id || p.employee || "",
          nombre:         p.nombre         || "",
          cargo:          p.cargo          || "",
          capacitacionOK: p.capacitacionOK || false,
          aptitudOK:      p.aptitudOK      || false,
        }))
      : [{ employee: "", nombre: "", cargo: "", capacitacionOK: false, aptitudOK: false }],
    vigia: permit?.vigia || "",
    ats: {
      toolboxFecha:       toDateTimeLocal(permit?.ats?.toolboxFecha),
      toolboxResponsable: permit?.ats?.toolboxResponsable || "",
      toolboxDuracion:    permit?.ats?.toolboxDuracion    ?? "",
      toolboxTemas:       permit?.ats?.toolboxTemas       || [],
      modo:               permit?.ats?.modo               || "jsa",
      vinculo:            permit?.ats?.vinculo            || "",
      vinculoLabel:       permit?.ats?.vinculoLabel       || "",
      pasos:              permit?.ats?.pasos?.length
        ? permit.ats.pasos.map(p => ({ ...EMPTY_PASO,
            paso:        p.paso        || "",
            categoria:   CATEGORIA_LEGACY[p.categoria] || p.categoria || "",
            peligro:     p.peligro     || "",
            sevIn:       p.sevIn       || 1,
            probIn:      p.probIn      || 1,
            jerarquia:   JERARQUIA_LEGACY[p.jerarquia] || p.jerarquia || "",
            controles:   p.controles   || "",
            sevRes:      p.sevRes      || 1,
            probRes:     p.probRes     || 1,
            responsable: p.responsable || "",
          }))
        : [{ ...EMPTY_PASO }],
      archivoNombre:      permit?.ats?.archivoNombre      || "",
      externoQuien:       permit?.ats?.externoQuien       || "",
      externoFecha:       permit?.ats?.externoFecha
        ? new Date(permit.ats.externoFecha).toISOString().slice(0, 10)
        : "",
    },
    epp:       permit?.epp       || [],
    eppPreUso: permit?.eppPreUso ?? false,
    lmra: {
      fecha:     permit?.lmra?.fecha ? toDateTimeLocal(permit.lmra.fecha) : "",
      preguntas: LMRA_PREGUNTAS.map((_, i) => ({
        respuesta:   permit?.lmra?.preguntas?.[i]?.respuesta ?? null,
        observacion: permit?.lmra?.preguntas?.[i]?.observacion || "",
      })),
    },
    flujoAprobacion: permit?.flujoAprobacion?.length
      ? permit.flujoAprobacion.map(n => ({ ...n, suplentes: [...(n.suplentes || [])] }))
      : DEFAULT_FLUJO_LEVELS.map(n => ({ ...n, suplentes: [...n.suplentes] })),
    tiposData: initTiposData(permit?.tiposData),
  });

  const loadSites = async () => {
    setLoadingSites(true);
    try {
      const sts = hasContextCompany
        ? await siteController.getSitesByCompany(accessToken, contextCompany, true)
        : await siteController.getSites(accessToken, true);
      setAllSites(sts.map(s => ({ value: s._id, label: s.name, country: s.country || "" })));
    } catch { /* ignore */ }
    finally { setLoadingSites(false); }
  };

  const loadSectors = async () => {
    setLoadingSectors(true);
    try {
      const secs = await sectorController.getSectors(accessToken, true);
      setSectors(secs.map(s => ({ value: s._id, label: s.name })));
    } catch { /* ignore */ }
    finally { setLoadingSectors(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [sts, secs, ars, usrs, emps] = await Promise.all([
          hasContextCompany
            ? siteController.getSitesByCompany(accessToken, contextCompany, true)
            : siteController.getSites(accessToken, true),
          sectorController.getSectors(accessToken, true),
          areaController.getAreas(accessToken, true),
          userController.getUsers(accessToken, true),
          employeeController.getEmployees(accessToken, { active: true }),
        ]);
        setAllSites(sts.map(s => ({ value: s._id, label: s.name, country: s.country || "" })));
        setSectors(secs.map(s => ({ value: s._id, label: s.name })));
        setAreas(ars.map(a => ({ value: a._id, label: a.name })));
        setUsers(usrs.map(u => ({
          value: u._id,
          label: `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.email,
        })));
        setEmployees(emps || []);
      } catch { /* ignore */ }
    })();
  }, [accessToken]);

  // Permiso nuevo con sitio elegido en la barra superior: el sitio se toma de ahí y no se edita
  const siteLocked = !permit && hasContextSite;
  useEffect(() => {
    if (siteLocked) setForm(p => (p.site === contextSite ? p : { ...p, site: contextSite }));
  }, [siteLocked, contextSite]);

  // El país sigue al sitio cuando el sitio tiene country cargado
  useEffect(() => {
    if (!form.site || !allSites.length) return;
    const siteObj = allSites.find(s => s.value === form.site);
    if (siteObj?.country) setForm(p => (p.pais === siteObj.country ? p : { ...p, pais: siteObj.country }));
  }, [allSites, form.site]);

  // Carga ApprovalConfig del sitio solo para permisos nuevos; no sobreescribe permisos existentes
  useEffect(() => {
    if (permit?._id) return;
    if (!form.site) return;
    (async () => {
      try {
        const cfg = await approvalConfigController.getConfig(accessToken, form.site);
        setApprovalCfg(cfg);
        if (cfg?.levels?.length) {
          setForm(prev => ({ ...prev, flujoAprobacion: mapConfigToFlujo(cfg.levels) }));
        }
      } catch { /* usa DEFAULT_FLUJO_LEVELS */ }
    })();
  }, [form.site]);

  // Carga EvRs cuando modo = vinculado y hay sitio seleccionado
  useEffect(() => {
    if (form.ats.modo !== "vinculado" || !form.site) { setEvrs([]); return; }
    setEvrsLoading(true);
    riskController.getRiskAssessments(accessToken, { site: form.site, active: true })
      .then(data => setEvrs(Array.isArray(data) ? data : []))
      .catch(() => setEvrs([]))
      .finally(() => setEvrsLoading(false));
  }, [form.ats.modo, form.site]);

  // Verifica stock de EPP seleccionados contra el módulo EPP
  useEffect(() => {
    if (form.epp.length === 0) { setEppStock({}); return; }
    const names = form.epp.map(v => v.includes(":") ? v.split(":").slice(1).join(":") : v);
    setEppStockLoading(true);
    eppApiController.checkEPPStock(accessToken, { names, site: form.site || undefined })
      .then(res => {
        const map = {};
        (res.result || []).forEach(r => { map[r.name] = r.status; });
        setEppStock(map);
      })
      .catch(() => setEppStock({}))
      .finally(() => setEppStockLoading(false));
  }, [form.epp, form.site]);

  useEffect(() => {
    if (openSections.sec6 && !form.lmra.fecha) {
      setForm(p => ({ ...p, lmra: { ...p.lmra, fecha: new Date().toISOString().slice(0, 16) } }));
    }
  }, [openSections.sec6]);


  const duracion      = useMemo(() => calcDuracion(form.startDateTime, form.estimatedEndDateTime), [form.startDateTime, form.estimatedEndDateTime]);
  const charCount     = form.descripcion.length;
  const fechaInvalid  = form.startDateTime && form.estimatedEndDateTime &&
    new Date(form.estimatedEndDateTime) <= new Date(form.startDateTime);
  const diferenteDia  = form.startDateTime && form.estimatedEndDateTime && !fechaInvalid &&
    new Date(form.startDateTime).toDateString() !== new Date(form.estimatedEndDateTime).toDateString();

  const isContratista = form.ejecutora === "contratista";

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handlePaisChange = (newPais) => set("pais", newPais);
  const requiereVigia = form.tipos.includes("espacio_confinado") || form.tipos.includes("caliente");

  const setContratista = (f, v) => setForm(p => ({ ...p, contratista: { ...p.contratista, [f]: v } }));
  const toggleContratistaDoc = (doc) => setForm(p => {
    const current = p.contratista.docs;
    const removing = current.includes(doc);
    const next = removing ? current.filter(d => d !== doc) : [...current, doc];
    const dv = { ...p.contratista.docsVencimiento };
    if (removing) delete dv[doc];
    return { ...p, contratista: { ...p.contratista, docs: next, docsVencimiento: dv } };
  });
  const setDocVencimiento = (docName, date) =>
    setForm(p => ({ ...p, contratista: { ...p.contratista, docsVencimiento: { ...p.contratista.docsVencimiento, [docName]: date } } }));
  const toggleTipo = (key) => setForm(p => ({
    ...p,
    tipos: p.tipos.includes(key) ? p.tipos.filter(t => t !== key) : [...p.tipos, key],
  }));

  const addPersonal = () => setForm(p => ({
    ...p,
    personal: [...p.personal, { employee: "", nombre: "", cargo: "", capacitacionOK: false, aptitudOK: false }],
  }));
  const removePersonal = (idx) => setForm(p => ({
    ...p,
    personal: p.personal.filter((_, i) => i !== idx),
  }));
  const updatePersonal = (idx, field, value) => setForm(p => {
    const next = [...p.personal];
    next[idx] = { ...next[idx], [field]: value };
    return { ...p, personal: next };
  });
  const setAts = (f, v) => setForm(p => ({ ...p, ats: { ...p.ats, [f]: v } }));
  const toggleToolboxTema = (tema) => setForm(p => {
    const curr = p.ats.toolboxTemas;
    const next = curr.includes(tema) ? curr.filter(t => t !== tema) : [...curr, tema];
    return { ...p, ats: { ...p.ats, toolboxTemas: next } };
  });
  const addPaso    = () => setForm(p => ({ ...p, ats: { ...p.ats, pasos: [...p.ats.pasos, { ...EMPTY_PASO }] } }));
  const removePaso = (idx) => setForm(p => ({ ...p, ats: { ...p.ats, pasos: p.ats.pasos.filter((_, i) => i !== idx) } }));
  const updatePaso = (idx, field, value) => setForm(p => {
    const next = [...p.ats.pasos];
    next[idx] = { ...next[idx], [field]: value };
    return { ...p, ats: { ...p.ats, pasos: next } };
  });

  const maxRiesgoResidual = useMemo(() =>
    form.ats.pasos.reduce((max, p) => Math.max(max, (p.sevRes || 1) * (p.probRes || 1)), 0),
    [form.ats.pasos]
  );

  const riskMatrixCells = useMemo(() => {
    const inherente = {};
    const residual  = {};
    form.ats.pasos.forEach(p => {
      if (p.probIn && p.sevIn) {
        const k = `${p.probIn}-${p.sevIn}`;
        inherente[k] = (inherente[k] || 0) + 1;
      }
      if (p.probRes && p.sevRes) {
        const k = `${p.probRes}-${p.sevRes}`;
        residual[k] = (residual[k] || 0) + 1;
      }
    });
    return { inherente, residual };
  }, [form.ats.pasos]);

  const eppSugeridos = useMemo(() => {
    const sugeridos = new Set();
    form.tipos.forEach(tipo => {
      (EPP_SUGERIDO_POR_TIPO[tipo] || []).forEach(e => sugeridos.add(e));
    });
    form.ats.pasos.forEach(p => {
      (EPP_SUGERIDO_POR_CATEGORIA[p.categoria] || []).forEach(e => sugeridos.add(e));
    });
    return sugeridos;
  }, [form.tipos, form.ats.pasos]);

  const toggleEpp = (val) => setForm(p => ({
    ...p,
    epp: p.epp.includes(val) ? p.epp.filter(e => e !== val) : [...p.epp, val],
  }));

  const setLmra = (f, v) => setForm(p => ({ ...p, lmra: { ...p.lmra, [f]: v } }));
  const updateLmraPregunta = (idx, field, value) => setForm(p => {
    const next = [...p.lmra.preguntas];
    next[idx] = { ...next[idx], [field]: value };
    return { ...p, lmra: { ...p.lmra, preguntas: next } };
  });

  const TIPO_BLOQUE_MAP = {
    altura: "alt", caliente: "cal", espacio_confinado: "ec",
    electrico_loto: "el", izaje: "iz", quimicos: "qu",
    excavacion: "ex", caldera: "cd", demolicion: "dm",
    acceso_techo: "ac", radiaciones: "rd",
  };

  const tiposDataTieneMal = useMemo(() => {
    for (const [tipo, bloque] of Object.entries(TIPO_BLOQUE_MAP)) {
      if (!form.tipos.includes(tipo)) continue;
      const b = form.tiposData[bloque];
      const campo = bloque === "el" ? "loto" : "controles";
      if (b[campo]?.some(c => c.estado === "mal")) return true;
    }
    return false;
  }, [form.tipos, form.tiposData]);

  const lmraBlocked  = !form.eppPreUso;
  const lmraTieneNo  = form.lmra.preguntas.some(p => p.respuesta === false);
  const lmraCompleta = !!(form.lmra.fecha && form.lmra.preguntas.every(p => p.respuesta !== null));
  // El LMRA se completa in-situ una sola vez: si ya vino guardado con el permiso, queda de solo lectura.
  const lmraSaved = !!permit?.lmra?.fecha;

  // EvR vinculada — detección de vencimiento
  const selectedEvr     = form.ats.modo === "vinculado" && form.ats.vinculo
    ? evrs.find(r => r._id === form.ats.vinculo) ?? null
    : null;
  const evrVencida      = !!(selectedEvr?.nextReassessment && new Date(selectedEvr.nextReassessment) < new Date());
  const showJsaSuggestion = evrVencida || condicionesChanged;

  const validationErrors = useMemo(() => {
    const e = [];
    if (!form.pais)              e.push("val_country");
    if (!form.site)              e.push("val_site");
    if (!form.sector)            e.push("val_sector");
    if (form.tipos.length === 0) e.push("val_work_type");
    if (charCount < 50)          e.push("val_description");
    if (!form.startDateTime)          e.push("val_start_date");
    if (!form.estimatedEndDateTime)   e.push("val_end_date");
    if (fechaInvalid)                 e.push("val_invalid_dates");
    if (!form.ejecutora)              e.push("val_executor");
    if (isContratista && !form.contratista.nombre)           e.push("val_company_name");
    if (isContratista && !form.contratista.doc)              e.push("val_tax_doc");
    if (isContratista && !form.contratista.supervisor)       e.push("val_contractor_supervisor");
    if (isContratista && form.contratista.docs.length === 0) e.push("val_enabling_docs");
    if (form.personal.length === 0)                          e.push("val_personnel");
    if (form.personal.some(p => !p.nombre.trim()))           e.push("val_personnel_names");
    if (requiereVigia && !form.vigia.trim())                 e.push("val_lookout");
    if (!form.ats.toolboxFecha)                              e.push("val_toolbox_date");
    if (!form.ats.toolboxResponsable.trim())                 e.push("val_toolbox_responsible");
    if (!form.ats.toolboxDuracion || Number(form.ats.toolboxDuracion) < 10) e.push("val_toolbox_duration");
    if (form.ats.toolboxTemas.length < 4)                    e.push("val_toolbox_topics");
    if (!form.ats.modo)                                      e.push("val_ats_mode");
    if (form.ats.modo === "vinculado" && !form.ats.vinculo)  e.push("val_evr_linked");
    if (form.ats.modo === "jsa") {
      if (form.ats.pasos.length === 0)
        e.push("val_jsa_steps");
      else if (form.ats.pasos.some(p => !p.paso.trim() || !p.categoria || !p.peligro.trim() || !p.jerarquia || !p.controles.trim()))
        e.push("val_jsa_incomplete");
      else if (form.ats.pasos.some(p => !p.sevRes || !p.probRes))
        e.push("val_jsa_residual");
    }
    if (form.ats.modo === "externo" && !form.ats.externoQuien.trim()) e.push("val_ats_responsible");
    if (form.ats.modo === "externo" && !form.ats.archivoNombre && !atsFile) e.push("val_ats_file");
    if (form.epp.length === 0)  e.push("val_epp");
    if (!form.eppPreUso)        e.push("val_epp_preuso");
    const eppFaltante = Object.values(eppStock).some(s => s === "falta");
    if (eppFaltante) e.push("val_epp_no_stock");
    if (form.flujoAprobacion.some(n => n.activo && n.acciones.length === 0)) e.push("val_flujo_acciones");
    if (form.flujoAprobacion.some(n => n.activo && n.criterio === "usuario" && !n.titular)) e.push("val_flujo_titular");
    return e;
  }, [form, charCount, fechaInvalid, isContratista, requiereVigia, atsFile]);

  const flujoReglas = useMemo(() => {
    const reglas = [];
    if (form.tipos.includes("espacio_confinado")) {
      const clase = form.tiposData.ec.clase;
      reglas.push({ id: "R1", msg: clase.startsWith("Clase A")
        ? "Espacio confinado Clase A (IDLH) → requiere aprobación adicional de Gerente del Sitio."
        : "Espacio confinado → puede requerir aprobación adicional de Gerente del Sitio." });
    }
    if (form.tipos.includes("electrico_loto")) {
      const t = form.tiposData.el.tension;
      reglas.push({ id: "R2", msg: (t.startsWith("MT") || t.startsWith("AT"))
        ? `Trabajo eléctrico ${t} → requiere Ingeniero eléctrico como aprobador adicional.`
        : "Trabajo eléctrico / LOTO → puede requerir Ingeniero eléctrico como aprobador adicional." });
    }
    if (form.tipos.includes("izaje")) {
      const cap = Number(form.tiposData.iz.capacidad);
      const car = Number(form.tiposData.iz.carga);
      const ratio = cap > 0 && car > 0 ? car / cap : 0;
      reglas.push({ id: "R3", msg: ratio > 0.75
        ? `Izaje al ${Math.round(ratio * 100)} % de la capacidad → requiere Ingeniero estructural adicional.`
        : "Izaje → si carga > 75 % de la capacidad nominal, requerirá Ingeniero estructural adicional." });
    }
    if (form.ejecutora === "contratista")
      reglas.push({ id: "R4", msg: "Permiso para contratista → notificación automática a Gestión de Contratistas (sin bloquear el flujo)." });
    if (form.ats.modo === "jsa" && maxRiesgoResidual >= 15)
      reglas.push({ id: "R5", msg: "Riesgo residual extremo (JSA ≥ 15) → requiere aprobación adicional de Gerente del Sitio." });
    return reglas;
  }, [form.tipos, form.ejecutora, form.ats.modo, maxRiesgoResidual,
      form.tiposData.ec.clase, form.tiposData.el.tension,
      form.tiposData.iz.capacidad, form.tiposData.iz.carga]);

  const updateFlujoNivel = (idx, field, value) => setForm(p => {
    const next = [...p.flujoAprobacion];
    next[idx] = { ...next[idx], [field]: value };
    return { ...p, flujoAprobacion: next };
  });

  const toggleFlujoAccion = (idx, accion) => setForm(p => {
    const curr = p.flujoAprobacion[idx].acciones;
    const next = curr.includes(accion) ? curr.filter(a => a !== accion) : [...curr, accion];
    const levels = [...p.flujoAprobacion];
    levels[idx] = { ...levels[idx], acciones: next };
    return { ...p, flujoAprobacion: levels };
  });

  const agregarNivelAprobacion = () => {
    if (form.flujoAprobacion.length >= 7) return;
    const nuevoNivel = {
      nivel: `Nivel ${form.flujoAprobacion.length} — Aprobador adicional`,
      rol: "Aprobador adicional",
      criterio: "usuario",
      titular: "",
      suplentes: [],
      acciones: ["aprobar", "aprobar_condiciones", "rechazar", "suspender", "solicitar_cambios"],
      activo: true,
      firmaDigital: true,
      comentarioObligatorio: true,
      requeridoPorTipo: false,
    };
    setForm(p => ({
      ...p,
      flujoAprobacion: [
        ...p.flujoAprobacion.slice(0, -1),
        nuevoNivel,
        p.flujoAprobacion[p.flujoAprobacion.length - 1],
      ],
    }));
  };

  const eliminarNivelAprobacion = (idx) => setForm(p => ({
    ...p,
    flujoAprobacion: p.flujoAprobacion.filter((_, i) => i !== idx),
  }));

  const addSuplente = (nivelIdx, nombre) => {
    if (!nombre) return;
    setForm(p => {
      const next = [...p.flujoAprobacion];
      const curr = next[nivelIdx].suplentes;
      if (curr.length >= 3 || curr.includes(nombre)) return p;
      next[nivelIdx] = { ...next[nivelIdx], suplentes: [...curr, nombre] };
      return { ...p, flujoAprobacion: next };
    });
  };

  const removeSuplente = (nivelIdx, nombre) => setForm(p => {
    const next = [...p.flujoAprobacion];
    next[nivelIdx] = { ...next[nivelIdx], suplentes: next[nivelIdx].suplentes.filter(s => s !== nombre) };
    return { ...p, flujoAprobacion: next };
  });

  const reiniciarFlujoDesdeTemplate = () => {
    if (!window.confirm("¿Reiniciar el flujo desde la plantilla del sitio? Se perderán los cambios actuales.")) return;
    const base = approvalCfg?.levels?.length
      ? mapConfigToFlujo(approvalCfg.levels)
      : DEFAULT_FLUJO_LEVELS.map(n => ({ ...n, suplentes: [...n.suplentes] }));
    setForm(p => ({ ...p, flujoAprobacion: base }));
  };

  const setTiposDato = (bloque, campo, valor) => setForm(p => ({
    ...p,
    tiposData: { ...p.tiposData, [bloque]: { ...p.tiposData[bloque], [campo]: valor } },
  }));

  const toggleTiposArray = (bloque, campo, val) => setForm(p => {
    const curr = p.tiposData[bloque][campo];
    const next = curr.includes(val) ? curr.filter(v => v !== val) : [...curr, val];
    return { ...p, tiposData: { ...p.tiposData, [bloque]: { ...p.tiposData[bloque], [campo]: next } } };
  });

  const updateTiposControl = (bloque, campo, idx, estado) => setForm(p => {
    const items = [...p.tiposData[bloque][campo]];
    items[idx] = { ...items[idx], estado };
    return { ...p, tiposData: { ...p.tiposData, [bloque]: { ...p.tiposData[bloque], [campo]: items } } };
  });

  useEffect(() => {
    if (form.tiposData.cd.ingresaPersonal && !form.tipos.includes("espacio_confinado")) {
      setForm(p => ({ ...p, tipos: [...p.tipos, "espacio_confinado"] }));
    }
  }, [form.tiposData.cd.ingresaPersonal]);

  const handlePersonalNombreChange = (idx, value) => {
    const fullName = (e) => `${e.nombre || ""} ${e.apellido || ""}`.trim();
    const match = employees.find(e => fullName(e).toLowerCase() === value.toLowerCase());
    setForm(p => {
      const next = [...p.personal];
      next[idx] = {
        ...next[idx],
        nombre:   value,
        employee: match?._id || "",
        cargo:    match ? (match.cargo || next[idx].cargo) : next[idx].cargo,
      };
      return { ...p, personal: next };
    });
  };

  const doSave = async (overrideStatus) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        ...(overrideStatus !== undefined ? { status: overrideStatus } : {}),
        sector:               form.sector        || undefined,
        area:                 form.area          || undefined,
        ehsSupervisor:        form.ehsSupervisor || undefined,
        startDateTime:        form.startDateTime        || undefined,
        estimatedEndDateTime: form.estimatedEndDateTime || undefined,
        contratista:          isContratista ? form.contratista : undefined,
        personal: form.personal.map(p => ({ ...p, employee: p.employee || undefined })),
      };
      let savedId = permit?._id;
      if (permit?._id) {
        await wpController.updateWorkPermit(accessToken, permit._id, payload);
        toast.success(overrideStatus === "requested" ? "Permiso enviado a aprobación." : "Permiso guardado.");
      } else {
        const result = await wpController.createWorkPermit(accessToken, payload);
        savedId = result?.workPermit?._id;
        toast.success(overrideStatus === "requested" ? "Permiso enviado a aprobación." : "Borrador guardado.");
      }
      if (atsFile && savedId) {
        try {
          const fd = new FormData();
          fd.append("file", atsFile);
          const up = await wpController.uploadFile(accessToken, savedId, fd);
          await wpController.updateWorkPermit(accessToken, savedId, {
            ats: { archivoNombre: up?.file?.key || up?.file?.name || atsFile.name },
          });
        } catch {
          toast.warning("Permiso guardado. Error al subir el archivo ATS.");
        }
      }
      if (fdsFile && savedId) {
        try {
          const fd2 = new FormData();
          fd2.append("file", fdsFile);
          const up2 = await wpController.uploadFile(accessToken, savedId, fd2);
          await wpController.updateWorkPermit(accessToken, savedId, {
            tiposData: { qu: { ...form.tiposData.qu, hojaSeguridad: up2?.file?.key || up2?.file?.name || fdsFile.name } },
          });
        } catch {
          toast.warning("Permiso guardado. Error al subir la hoja de seguridad.");
        }
      }
      onReload();
      close();
    } catch (err) {
      toast.error(err?.msg || "Error al guardar el permiso.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!form.site) return toast.warning("El sitio es obligatorio para guardar.");
    await doSave("draft");
  };

  const handleSendForApproval = async () => {
    if (!form.pais)              return toast.warning("El país es obligatorio.");
    if (!form.site)              return toast.warning("El sitio es obligatorio.");
    if (!form.sector)            return toast.warning("El sector es obligatorio.");
    if (form.tipos.length === 0) return toast.warning("Seleccioná al menos un tipo de trabajo.");
    if (charCount < 50)          return toast.warning(`Descripción mínima 50 caracteres. Actual: ${charCount}.`);
    if (!form.startDateTime)          return toast.warning("La fecha y hora de inicio son obligatorias.");
    if (!form.estimatedEndDateTime)   return toast.warning("La fecha y hora de cierre son obligatorias.");
    if (fechaInvalid)                 return toast.warning("La fecha de cierre debe ser posterior al inicio.");
    if (!form.ejecutora)              return toast.warning("La ejecutora es obligatoria.");
    if (isContratista && !form.contratista.nombre)     return toast.warning("La razón social del contratista es obligatoria.");
    if (isContratista && !form.contratista.doc)        return toast.warning("El documento fiscal es obligatorio.");
    if (isContratista && !form.contratista.supervisor) return toast.warning("El supervisor del contratista es obligatorio.");
    if (isContratista && form.contratista.docs.length === 0) return toast.warning("Seleccioná al menos un documento habilitante del contratista.");
    if (isContratista && form.contratista.docs.length > 0) {
      const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
      const vencido = form.contratista.docs.find(d => {
        const v = form.contratista.docsVencimiento?.[d];
        return v && new Date(v) < hoy;
      });
      if (vencido) return toast.warning(`Documentación del contratista vencida: "${vencido}".`);
    }
    if (form.personal.length === 0) return toast.warning("Agregá al menos una persona al personal autorizado.");
    if (form.personal.some(p => !p.nombre.trim())) return toast.warning("Completá el nombre de todas las personas del personal autorizado.");
    if (requiereVigia && !form.vigia.trim()) return toast.warning("El vigía es obligatorio para trabajos en espacio confinado o en caliente.");
    if (!form.ats.toolboxFecha)                          return toast.warning("La fecha del toolbox talk es obligatoria.");
    if (!form.ats.toolboxResponsable.trim())             return toast.warning("El responsable del toolbox es obligatorio.");
    if (!form.ats.toolboxDuracion)                       return toast.warning("La duración del toolbox es obligatoria.");
    if (Number(form.ats.toolboxDuracion) < 10)           return toast.warning("La duración mínima del toolbox es 10 minutos.");
    if (form.ats.toolboxTemas.length < 4)                return toast.warning(`Seleccioná al menos 4 temas del toolbox. Actuales: ${form.ats.toolboxTemas.length}.`);
    if (!form.ats.modo) return toast.warning("Seleccioná la modalidad de ATS/JSA.");
    if (form.ats.modo === "vinculado" && !form.ats.vinculo)
      return toast.warning("Seleccioná el EvR vinculado.");
    if (form.ats.modo === "jsa") {
      if (form.ats.pasos.length === 0)
        return toast.warning("Agregá al menos un paso al JSA.");
      const pasoIncompleto = form.ats.pasos.find(
        p => !p.paso.trim() || !p.categoria || !p.peligro.trim() || !p.jerarquia || !p.controles.trim()
      );
      if (pasoIncompleto)
        return toast.warning("Completá todos los campos obligatorios de cada paso del JSA (paso, categoría, peligro, jerarquía y controles).");
      const pasoSinResidual = form.ats.pasos.find(p => !p.sevRes || !p.probRes);
      if (pasoSinResidual)
        return toast.warning("Completá el riesgo residual (severidad y probabilidad) de todos los pasos del JSA.");
    }
    if (form.ats.modo === "externo" && !form.ats.externoQuien.trim())
      return toast.warning("Indicá el responsable del ATS externo.");
    if (form.ats.modo === "externo" && !form.ats.archivoNombre && !atsFile)
      return toast.warning("Adjuntá el archivo del ATS firmado.");
    if (atsFile && atsFile.size > 10 * 1024 * 1024)
      return toast.warning("El archivo supera los 10 MB.");
    if (form.epp.length === 0)
      return toast.warning("Seleccioná al menos un EPP requerido para el trabajo.");
    if (!form.eppPreUso)
      return toast.warning("Confirmá la verificación pre-uso del EPP antes de guardar.");
    if (form.flujoAprobacion.some(n => n.activo && n.acciones.length === 0))
      return toast.warning("Cada nivel activo del flujo de aprobación debe tener al menos una acción permitida.");
    const sinTitular = form.flujoAprobacion.find(n => n.activo && n.criterio === "usuario" && !n.titular);
    if (sinTitular)
      return toast.warning(`Seleccioná el titular para el nivel "${sinTitular.nivel}".`);
    if (fdsFile && fdsFile.size > 5 * 1024 * 1024)
      return toast.warning("La hoja de seguridad (FDS) supera los 5 MB.");
    await doSave("requested");
  };

  const handleSaveChanges = async () => {
    if (!form.site) return toast.warning("El sitio es obligatorio.");
    if (form.tipos.includes("espacio_confinado") && form.status !== "draft") {
      const ec = form.tiposData.ec;
      const gasFields = ["o2", "lel", "h2s", "co"];
      const sinMedicion = gasFields.every(f => ec[f] === "" || ec[f] === null || ec[f] === undefined);
      const hayPeligro  = gasFields.some(f => gasStatus(f, ec[f]) === "danger");
      if (sinMedicion)
        return toast.warning("Espacio confinado: registrá las lecturas atmosféricas (O₂, LEL, H₂S, CO) antes de continuar.");
      if (hayPeligro)
        return toast.warning("Espacio confinado: atmósfera no apta — hay lecturas en rango peligroso. No iniciar el trabajo hasta corregir.");
    }
    if (tiposDataTieneMal && form.status !== "draft")
      return toast.warning("Hay controles en estado 'Mal' en los bloques por tipo. Corregí las condiciones antes de cambiar el estado del permiso.");
    if (form.status === "in_execution" && !lmraCompleta)
      return toast.warning("El LMRA debe estar completo (fecha + las 4 preguntas respondidas) para iniciar el trabajo.");
    if (form.status === "in_execution" && lmraTieneNo)
      return toast.warning("El LMRA tiene una o más respuestas negativas. Corregí las condiciones en el sitio antes de iniciar el trabajo.");
    await doSave();
  };

  const NativeSelect = ({ value, onChange, options, placeholder, disabled, addOption }) => (
    <select
      className={`wpf-select${disabled ? " wpf-select--disabled" : ""}`}
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">{placeholder || "— Seleccionar —"}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      {addOption && <option value={addOption.value}>{addOption.label}</option>}
    </select>
  );

  const permitNumber = permit?._id && permit?.customId
    ? `PT-${new Date(permit.createdAt || Date.now()).getFullYear()}-${String(permit.customId).padStart(5, "0")}`
    : null;

  return (
    <div className="wpf">
      {/* ── CABECERA (estilo drawer Gestión de Accidentes) ── */}
      <div className="wpf-drawer-header">
        <div className="wpf-drawer-header__left">
          <span className="wpf-drawer-header__id-pill">
            {permitNumber ?? `PT-${new Date().getFullYear()}-?????`}
          </span>
          <div>
            <div className="wpf-drawer-header__title">
              {permit?._id ? t("edit_work_permit") : t("new_work_permit")}
            </div>
            <div className="wpf-drawer-header__subtitle">
              {t("wp_form_subtitle")}
            </div>
          </div>
        </div>
        <button className="wpf-drawer-header__close-btn" onClick={close} type="button">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div className="wpf__scroll">

        <p className="wpf-hint-line">
          {t("wp_form_hint")}
        </p>

        {/* ── SECCIÓN 1: IDENTIFICACIÓN ─────────────────── */}
        <div>
          <Section title={t("wp_sec_identification")}
            open={openSections.sec1} onToggle={() => toggleSection("sec1")}>

            <div className="wpf-row-3">
              <div className="wpf-field">
                <FieldLabel required>{t("country")}</FieldLabel>
                <NativeSelect value={form.pais} onChange={handlePaisChange}
                  options={PAIS_OPTIONS} placeholder={t("wp_select_country_placeholder")} />
                {PAIS_LEGAL[form.pais] ? (
                  <div className="wpf-pais-hints">
                    <p className="wpf-legal-hint">{PAIS_LEGAL[form.pais]}</p>
                    {PAIS_ASEGURADORA[form.pais] && (
                      <p className="wpf-legal-hint">
                        <strong>{t("wp_insurer")}:</strong> {PAIS_ASEGURADORA[form.pais]}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="wpf-legal-hint wpf-legal-hint--placeholder">
                    {t("wp_select_country_hint")}
                  </p>
                )}
              </div>
              <div className="wpf-field">
                <FieldLabel required>{t("site")}</FieldLabel>
                {siteLocked ? (
                  <>
                    <NativeSelect
                      value={form.site}
                      onChange={() => {}}
                      options={allSites.filter(s => s.value === form.site)}
                      placeholder={t("wp_select_placeholder")}
                      disabled
                    />
                    <p className="wpf-legal-hint">{t("wp_site_from_header")}</p>
                  </>
                ) : (
                  <>
                    <div className="wpf-site-row">
                      <NativeSelect
                        value={form.site}
                        onChange={v => {
                          if (v === "__add_site__") { setShowSiteModal(true); return; }
                          set("site", v);
                        }}
                        options={allSites}
                        addOption={canManageCatalogs ? { value: "__add_site__", label: t("wp_add_new_site") } : null}
                        placeholder={allSites.length === 0 ? t("wp_no_sites") : t("wp_select_placeholder")}
                      />
                      <button type="button" className="wpf-reload-btn" title={t("wp_reload_sites")}
                        disabled={loadingSites} onClick={loadSites}>
                        {loadingSites ? "…" : "↺"}
                      </button>
                    </div>
                    {allSites.length === 0 && (
                      <p className="wpf-legal-hint wpf-legal-hint--warn">
                        {canManageCatalogs ? t("wp_no_sites_admin") : t("wp_no_sites_user")}
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="wpf-field">
                <FieldLabel required>{t("sector")}</FieldLabel>
                <div className="wpf-site-row">
                  <NativeSelect
                    value={form.sector}
                    onChange={v => {
                      if (v === "__add_sector__") { setShowSectorModal(true); return; }
                      set("sector", v);
                    }}
                    options={sectors}
                    addOption={canManageCatalogs ? { value: "__add_sector__", label: t("wp_add_new_sector") } : null}
                    placeholder={t("wp_select_placeholder")}
                  />
                  <button type="button" className="wpf-reload-btn" title={t("wp_reload_sectors")}
                    disabled={loadingSectors} onClick={loadSectors}>
                    {loadingSectors ? "…" : "↺"}
                  </button>
                </div>
                {sectors.length === 0 && (
                  <p className="wpf-legal-hint wpf-legal-hint--warn">
                    {canManageCatalogs ? t("wp_no_sectors_admin") : t("wp_no_sectors_user")}
                  </p>
                )}
              </div>
            </div>

            <div className="wpf-field" style={{ marginTop: "1rem" }}>
              <FieldLabel>{t("wp_exact_location")}</FieldLabel>
              <input className="wpf-input" maxLength={150} value={form.ubicacion}
                placeholder={t("wp_exact_location_placeholder")}
                onChange={e => set("ubicacion", e.target.value)} />
            </div>

            <div className="wpf-field" style={{ marginTop: "1rem" }}>
              <FieldLabel required>{t("wp_permit_type")} <span className="wpf-sub">({t("wp_multiple_selection")})</span></FieldLabel>
              <div className="wpf-tipos-grid">
                {TIPOS_OPTIONS.map(({ key }) => {
                  const norma = form.pais && NORMA_POR_PAIS[form.pais]?.[key];
                  return (
                    <label key={key} className={`wpf-tipo-card${form.tipos.includes(key) ? " selected" : ""}`}>
                      <input type="checkbox" checked={form.tipos.includes(key)} onChange={() => toggleTipo(key)} />
                      <span className="wpf-tipo-card__text">
                        <span>{t("wp_type_" + key)}</span>
                        {norma && <span className="wpf-tipo-card__norma">{norma}</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
              {form.tipos.length === 0 && <p className="wpf-warn">{t("wp_select_at_least_one_type")}</p>}
            </div>

            {/* ── Bloques condicionales por tipo ── */}
            {form.tipos.length > 0 && (
              <div className="wpf-tipo-bloques">

                {/* Bloque A — Altura */}
                {form.tipos.includes("altura") && (() => {
                  const b = form.tiposData.alt;
                  const minM = ALTURA_MIN_PAIS[form.pais] || 1.8;
                  const tieneMal = b.controles.some(c => c.estado === "mal");
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">A</span>
                        <span className="wpf-tipo-bloque__title">Trabajo en altura — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        <div className="wpf-row-2">
                          <div className="wpf-field">
                            <FieldLabel required>Altura máxima de trabajo (m)</FieldLabel>
                            <input type="number" className="wpf-input" min={0} step={0.1}
                              value={b.altura} placeholder={`Mín. ${minM} m en ${form.pais || "este país"}`}
                              onChange={e => setTiposDato("alt", "altura", e.target.value)} />
                            {b.altura !== "" && Number(b.altura) < minM && (
                              <p className="wpf-warn">Bajo el umbral legal ({minM} m). Verificá si aplica permiso.</p>
                            )}
                          </div>
                          <div className="wpf-field">
                            <FieldLabel required>Medios de acceso <span className="wpf-sub">(selección múltiple)</span></FieldLabel>
                            <div className="wpf-docs-grid" style={{ maxHeight: 160, overflowY: "auto" }}>
                              {ALTURA_MEDIOS.map(m => (
                                <label key={m} className={`wpf-doc-card${b.medios.includes(m) ? " selected" : ""}`}>
                                  <input type="checkbox" checked={b.medios.includes(m)}
                                    onChange={() => toggleTiposArray("alt", "medios", m)} />
                                  <span>{m}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div>
                          <FieldLabel required>Controles de seguridad</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.controles.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("alt", "controles", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Controles en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bloque B — Caliente */}
                {form.tipos.includes("caliente") && (() => {
                  const b = form.tiposData.cal;
                  const tieneMal = b.controles.some(c => c.estado === "mal");
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">B</span>
                        <span className="wpf-tipo-bloque__title">Trabajo en caliente — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        <div className="wpf-field">
                          <FieldLabel required>Subtipos de trabajo en caliente</FieldLabel>
                          <div className="wpf-docs-grid">
                            {CALIENTE_SUBTIPOS.map(s => (
                              <label key={s} className={`wpf-doc-card${b.subtipos.includes(s) ? " selected" : ""}`}>
                                <input type="checkbox" checked={b.subtipos.includes(s)}
                                  onChange={() => toggleTiposArray("cal", "subtipos", s)} />
                                <span>{s}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <FieldLabel required>Controles de seguridad</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.controles.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("cal", "controles", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Controles en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bloque C — Espacio Confinado */}
                {form.tipos.includes("espacio_confinado") && (() => {
                  const b = form.tiposData.ec;
                  const tieneMal = b.controles.some(c => c.estado === "mal");
                  const gasFields = ["o2", "lel", "h2s", "co"];
                  const gasBlocker = gasFields.some(f => {
                    const st = gasStatus(f, b[f]);
                    return st === "danger";
                  });
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">C</span>
                        <span className="wpf-tipo-bloque__title">Espacio confinado — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        <div className="wpf-row-2">
                          <div className="wpf-field">
                            <FieldLabel required>Identificación del espacio</FieldLabel>
                            <input className="wpf-input" maxLength={200}
                              placeholder="Ej.: Tanque T-03, Cisterna sur, Silo 2"
                              value={b.identificacion}
                              onChange={e => setTiposDato("ec", "identificacion", e.target.value)} />
                          </div>
                          <div className="wpf-field">
                            <FieldLabel required>Clase del espacio confinado</FieldLabel>
                            <select className="wpf-select" value={b.clase}
                              onChange={e => {
                                const cls = e.target.value;
                                setTiposDato("ec", "clase", cls);
                                if (EC_FRECUENCIA_POR_CLASE[cls]) setTiposDato("ec", "frecuencia", EC_FRECUENCIA_POR_CLASE[cls]);
                              }}>
                              <option value="">— Seleccionar —</option>
                              {EC_CLASES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="wpf-row-3">
                          <div className="wpf-field">
                            <FieldLabel required>Equipo de medición (modelo)</FieldLabel>
                            <input className="wpf-input" maxLength={200}
                              placeholder="Ej.: BW GasAlert Max XT II"
                              value={b.equipo}
                              onChange={e => setTiposDato("ec", "equipo", e.target.value)} />
                          </div>
                          <div className="wpf-field">
                            <FieldLabel required>Última calibración</FieldLabel>
                            <input type="date" className="wpf-input"
                              value={b.calibracion}
                              onChange={e => setTiposDato("ec", "calibracion", e.target.value)} />
                          </div>
                          <div className="wpf-field">
                            <FieldLabel required>Fecha / hora medición</FieldLabel>
                            <input type="datetime-local" className="wpf-input"
                              value={b.horaMed}
                              onChange={e => setTiposDato("ec", "horaMed", e.target.value)} />
                          </div>
                        </div>
                        {/* Lecturas de gas */}
                        <div>
                          <FieldLabel required>Lecturas atmosféricas</FieldLabel>
                          <div className="wpf-gas-grid">
                            {gasFields.map(f => {
                              const st = gasStatus(f, b[f]);
                              return (
                                <div key={f} className="wpf-gas-row">
                                  <span className="wpf-gas-label">{GAS_LABELS[f]}</span>
                                  <div className="wpf-gas-input-row">
                                    <input type="number" className="wpf-input" step="0.1"
                                      placeholder="—" value={b[f]}
                                      onChange={e => setTiposDato("ec", f, e.target.value)} />
                                    {st && <span className={`wpf-gas-badge wpf-gas-badge--${st}`}>
                                      {st === "ok" ? "OK" : st === "warn" ? "⚠ Alerta" : "✗ Peligro"}
                                    </span>}
                                  </div>
                                  <span className="wpf-gas-limit">{GAS_LIMITS[f]}</span>
                                </div>
                              );
                            })}
                          </div>
                          {gasBlocker && (
                            <p className="wpf-bloque-mal-warn" style={{ marginTop: "0.4rem" }}>
                              ⚠ Lectura(s) en rango peligroso — no ingresar al espacio. Ventilación y nueva medición obligatorias.
                            </p>
                          )}
                        </div>
                        <div className="wpf-field">
                          <FieldLabel required>Frecuencia de monitoreo</FieldLabel>
                          <select className="wpf-select" value={b.frecuencia}
                            onChange={e => setTiposDato("ec", "frecuencia", e.target.value)}>
                            <option value="">— Seleccionar —</option>
                            {["Continuo", "Cada 30 min", "Cada 60 min", "Al inicio y al final"].map(f => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <FieldLabel required>Controles de seguridad</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.controles.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("ec", "controles", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Controles en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bloque D — Eléctrico / LOTO */}
                {form.tipos.includes("electrico_loto") && (() => {
                  const b = form.tiposData.el;
                  const tieneMal = b.loto.some(c => c.estado === "mal");
                  const eppSug = EL_EPP_POR_TENSION[b.tension] || [];
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">D</span>
                        <span className="wpf-tipo-bloque__title">Eléctrico / LOTO — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        <div className="wpf-field">
                          <FieldLabel required>Nivel de tensión</FieldLabel>
                          <select className="wpf-select" value={b.tension}
                            onChange={e => {
                              const t = e.target.value;
                              setTiposDato("el", "tension", t);
                              setTiposDato("el", "epp", EL_EPP_POR_TENSION[t] || []);
                            }}>
                            <option value="">— Seleccionar —</option>
                            {EL_TENSION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        {eppSug.length > 0 && (
                          <p className="wpf-legal-hint wpf-legal-hint--info">
                            💡 EPP sugerido para {b.tension}: {eppSug.join(" · ")}
                          </p>
                        )}
                        <div>
                          <FieldLabel required>Bloqueo LOTO — puntos de energía</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.loto.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("el", "loto", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Puntos LOTO en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bloque E — Izaje */}
                {form.tipos.includes("izaje") && (() => {
                  const b = form.tiposData.iz;
                  const tieneMal = b.controles.some(c => c.estado === "mal");
                  const cap = Number(b.capacidad);
                  const car = Number(b.carga);
                  const ratio = cap > 0 && car > 0 ? car / cap : null;
                  const ratioCls = ratio === null ? null : ratio > 0.9 ? "crit" : ratio > 0.75 ? "warn" : "ok";
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">E</span>
                        <span className="wpf-tipo-bloque__title">Izaje — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        <div className="wpf-row-3">
                          <div className="wpf-field">
                            <FieldLabel required>Equipo de izaje</FieldLabel>
                            <input className="wpf-input" maxLength={200}
                              placeholder="Ej.: Grúa torre Liebherr 110 EC-B"
                              value={b.equipo}
                              onChange={e => setTiposDato("iz", "equipo", e.target.value)} />
                          </div>
                          <div className="wpf-field">
                            <FieldLabel required>Capacidad nominal (ton)</FieldLabel>
                            <input type="number" className="wpf-input" min={0} step={0.1}
                              value={b.capacidad}
                              onChange={e => setTiposDato("iz", "capacidad", e.target.value)} />
                          </div>
                          <div className="wpf-field">
                            <FieldLabel required>Carga estimada (ton)</FieldLabel>
                            <input type="number" className="wpf-input" min={0} step={0.1}
                              value={b.carga}
                              onChange={e => setTiposDato("iz", "carga", e.target.value)} />
                          </div>
                        </div>
                        {ratio !== null && (
                          <div className="wpf-izaje-ratio">
                            <div className="wpf-izaje-ratio__bar-wrap">
                              <div className={`wpf-izaje-ratio__bar wpf-izaje-ratio__bar--${ratioCls}`}
                                style={{ width: `${Math.min(ratio * 100, 100)}%` }} />
                            </div>
                            <span className={`wpf-izaje-ratio__label${ratioCls === "crit" ? " wpf-warn" : ratioCls === "warn" ? " wpf-hint" : ""}`}>
                              {Math.round(ratio * 100)} % de la capacidad nominal
                              {ratio > 0.75 ? " — requiere Ingeniero estructural" : ""}
                            </span>
                          </div>
                        )}
                        <div>
                          <FieldLabel required>Controles de seguridad</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.controles.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("iz", "controles", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Controles en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bloque F — Químicos */}
                {form.tipos.includes("quimicos") && (() => {
                  const b = form.tiposData.qu;
                  const tieneMal = b.controles.some(c => c.estado === "mal");
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">F</span>
                        <span className="wpf-tipo-bloque__title">Químicos / Sustancias peligrosas — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        <div className="wpf-field">
                          <FieldLabel required>Sustancia(s) peligrosa(s) involucrada(s)</FieldLabel>
                          <input className="wpf-input" maxLength={300}
                            placeholder="Ej.: Ácido sulfúrico H2SO4 concentrado, Tolueno, NaOH al 30%"
                            value={b.sustancia}
                            onChange={e => setTiposDato("qu", "sustancia", e.target.value)} />
                        </div>
                        <div className="wpf-field">
                          <FieldLabel required>
                            Hoja de datos de seguridad (FDS/SDS){" "}
                            <span className="wpf-sub">PDF · máx 5 MB</span>
                          </FieldLabel>
                          {b.hojaSeguridad ? (
                            <div className="wpf-ats-file-existing">
                              <span>📎 {b.hojaSeguridad}</span>
                              <button type="button" className="wpf-personal-table__remove"
                                onClick={() => { setTiposDato("qu", "hojaSeguridad", ""); setFdsFile(null); }}>✕</button>
                            </div>
                          ) : (
                            <>
                              <input type="file" accept=".pdf" className="wpf-input"
                                onChange={e => {
                                  const f = e.target.files?.[0] || null;
                                  if (f && f.size > 5 * 1024 * 1024) {
                                    toast.warning("La FDS supera los 5 MB.");
                                    e.target.value = "";
                                    return;
                                  }
                                  setFdsFile(f);
                                }} />
                              {fdsFile && (
                                <p className="wpf-legal-hint">📎 {fdsFile.name} — {(fdsFile.size / 1024 / 1024).toFixed(2)} MB</p>
                              )}
                            </>
                          )}
                        </div>
                        <div>
                          <FieldLabel required>Controles de seguridad</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.controles.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("qu", "controles", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Controles en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bloque G — Excavación */}
                {form.tipos.includes("excavacion") && (() => {
                  const b = form.tiposData.ex;
                  const tieneMal = b.controles.some(c => c.estado === "mal");
                  const minM = EXCAV_MIN_PAIS[form.pais] || 1.5;
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">G</span>
                        <span className="wpf-tipo-bloque__title">Excavación — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        <div className="wpf-field" style={{ maxWidth: 240 }}>
                          <FieldLabel required>Profundidad máxima (m)</FieldLabel>
                          <input type="number" className="wpf-input" min={0} step={0.1}
                            value={b.profundidad}
                            placeholder={`Umbral permiso: ${minM} m`}
                            onChange={e => setTiposDato("ex", "profundidad", e.target.value)} />
                          {b.profundidad !== "" && Number(b.profundidad) >= minM && (
                            <p className="wpf-hint">Profundidad ≥ {minM} m — entibado o análisis geotécnico obligatorio.</p>
                          )}
                        </div>
                        <div>
                          <FieldLabel required>Controles de seguridad</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.controles.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("ex", "controles", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Controles en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bloque H — Caldera */}
                {form.tipos.includes("caldera") && (() => {
                  const b = form.tiposData.cd;
                  const tieneMal = b.controles.some(c => c.estado === "mal");
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">H</span>
                        <span className="wpf-tipo-bloque__title">Caldera / Recipiente a presión — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        <div className="wpf-row-2">
                          <div className="wpf-field">
                            <FieldLabel required>Identificación de la caldera</FieldLabel>
                            <input className="wpf-input" maxLength={200}
                              placeholder="Ej.: Caldera pirotubular Cleaver-Brooks CB-200 S/N 12345"
                              value={b.identificacion}
                              onChange={e => setTiposDato("cd", "identificacion", e.target.value)} />
                          </div>
                          <div className="wpf-field">
                            <FieldLabel required>Presión de operación (bar)</FieldLabel>
                            <input type="number" className="wpf-input" min={0} step={0.1}
                              value={b.presion}
                              onChange={e => setTiposDato("cd", "presion", e.target.value)} />
                          </div>
                        </div>
                        <label className="wpf-flujo-opt-check" style={{ marginTop: "0.2rem" }}>
                          <input type="checkbox" checked={b.ingresaPersonal}
                            onChange={e => setTiposDato("cd", "ingresaPersonal", e.target.checked)} />
                          Personal ingresa al interior de la caldera / recipiente
                        </label>
                        {b.ingresaPersonal && (
                          <p className="wpf-legal-hint wpf-legal-hint--warn">
                            ⚠ Personal ingresa al interior — se agrega automáticamente <strong>Espacio confinado</strong> a los tipos del permiso.
                          </p>
                        )}
                        <div>
                          <FieldLabel required>Controles de seguridad</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.controles.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("cd", "controles", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Controles en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bloque I — Demolición */}
                {form.tipos.includes("demolicion") && (() => {
                  const b = form.tiposData.dm;
                  const tieneMal = b.controles.some(c => c.estado === "mal");
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">I</span>
                        <span className="wpf-tipo-bloque__title">Demolición / Derribo — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        <div className="wpf-field">
                          <FieldLabel required>Área / estructura a demoler</FieldLabel>
                          <input className="wpf-input" maxLength={200}
                            placeholder="Ej.: Muro perimetral sector norte, Losa entre pisos 2 y 3"
                            value={b.area}
                            onChange={e => setTiposDato("dm", "area", e.target.value)} />
                        </div>
                        <div>
                          <FieldLabel required>Controles de seguridad</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.controles.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("dm", "controles", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Controles en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bloque J — Acceso a techo */}
                {form.tipos.includes("acceso_techo") && (() => {
                  const b = form.tiposData.ac;
                  const tieneMal = b.controles.some(c => c.estado === "mal");
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">J</span>
                        <span className="wpf-tipo-bloque__title">Acceso a techo / cubierta — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        <div className="wpf-field" style={{ maxWidth: 300 }}>
                          <FieldLabel required>Tipo de cubierta</FieldLabel>
                          <select className="wpf-select" value={b.tipoTecho}
                            onChange={e => setTiposDato("ac", "tipoTecho", e.target.value)}>
                            <option value="">— Seleccionar —</option>
                            {AC_TIPO_TECHO.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          {b.tipoTecho === "Frágil / Translúcido" && (
                            <p className="wpf-warn">Cubierta frágil — acceso solo con plataforma o tablones certificados.</p>
                          )}
                        </div>
                        <div>
                          <FieldLabel required>Controles de seguridad</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.controles.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("ac", "controles", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Controles en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Bloque K — Radiaciones */}
                {form.tipos.includes("radiaciones") && (() => {
                  const b = form.tiposData.rd;
                  const tieneMal = b.controles.some(c => c.estado === "mal");
                  const esIonizante = b.tipo.includes("Ionizante");
                  return (
                    <div className="wpf-tipo-bloque">
                      <div className="wpf-tipo-bloque__header">
                        <span className="wpf-tipo-bloque__badge">K</span>
                        <span className="wpf-tipo-bloque__title">Radiaciones — datos específicos</span>
                      </div>
                      <div className="wpf-tipo-bloque__body">
                        {esIonizante && (
                          <p className="wpf-legal-hint wpf-legal-hint--warn">
                            ⚠ Radiación ionizante — verificá que la autorización de la autoridad nuclear ({form.pais || "local"}) esté vigente antes de iniciar el trabajo.
                          </p>
                        )}
                        <div className="wpf-row-2">
                          <div className="wpf-field">
                            <FieldLabel required>Tipo de radiación</FieldLabel>
                            <select className="wpf-select" value={b.tipo}
                              onChange={e => setTiposDato("rd", "tipo", e.target.value)}>
                              <option value="">— Seleccionar —</option>
                              {RD_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div className="wpf-field">
                            <FieldLabel required>Fuente / equipo emisor</FieldLabel>
                            <input className="wpf-input" maxLength={300}
                              placeholder="Ej.: Fuente Ir-192 N/S 4521, Equipo Rx Philips HD350"
                              value={b.fuente}
                              onChange={e => setTiposDato("rd", "fuente", e.target.value)} />
                          </div>
                        </div>
                        <div>
                          <FieldLabel required>Controles de seguridad</FieldLabel>
                          <div className="wpf-controles-list">
                            {b.controles.map((c, i) => (
                              <div key={i} className={`wpf-control-row${c.estado === "mal" ? " wpf-control-row--mal" : ""}`}>
                                <span className="wpf-control-row__label">{c.item}</span>
                                <TriToggle value={c.estado} onChange={v => updateTiposControl("rd", "controles", i, v)} />
                              </div>
                            ))}
                          </div>
                          {tieneMal && <p className="wpf-bloque-mal-warn">⚠ Controles en estado Mal bloquearán el cambio de estado del permiso.</p>}
                        </div>
                      </div>
                    </div>
                  );
                })()}

              </div>
            )}

            <div className="wpf-field" style={{ marginTop: "1rem" }}>
              <FieldLabel required>{t("wp_work_description")}</FieldLabel>
              <textarea className="wpf-textarea" rows={3} maxLength={1000}
                placeholder={t("wp_description_placeholder")}
                value={form.descripcion} onChange={e => set("descripcion", e.target.value)} />
              <p className={`wpf-char-count${charCount < 50 ? " warn" : ""}`}>
                {charCount}/1000{charCount < 50 ? ` ${t("wp_min_50_chars")}` : ""}
              </p>
            </div>

            <div className="wpf-field" style={{ marginTop: "1rem" }}>
              <FieldLabel required>{t("executor")}</FieldLabel>
              <NativeSelect value={form.ejecutora} onChange={v => set("ejecutora", v)}
                options={[
                  { value: "personal_propio", label: t("wp_own_staff") },
                  { value: "contratista",     label: t("wp_contractor_option") },
                ]} placeholder={t("wp_select_placeholder")} />
            </div>

            <div className="wpf-row-2" style={{ marginTop: "1rem" }}>
              <div className="wpf-field">
                <FieldLabel required>{t("wp_start_datetime")}</FieldLabel>
                <input type="datetime-local" className="wpf-input" value={form.startDateTime}
                  onChange={e => set("startDateTime", e.target.value)} />
              </div>
              <div className="wpf-field">
                <FieldLabel required>{t("wp_end_datetime")}</FieldLabel>
                <input type="datetime-local" className="wpf-input" value={form.estimatedEndDateTime}
                  onChange={e => set("estimatedEndDateTime", e.target.value)} />
                {duracion && !fechaInvalid && <p className="wpf-hint">{t("wp_duration")}: {duracion}</p>}
                {fechaInvalid && <p className="wpf-warn">{t("wp_date_invalid")}</p>}
              </div>
            </div>

            <div className="wpf-vigencia-banner">
              ⚠️ {t("wp_validity_rule")}
              {diferenteDia && (
                <span className="wpf-vigencia-banner__alerta">
                  {" "}{t("wp_validity_alert")}
                </span>
              )}
            </div>

            <div className="wpf-row-2" style={{ marginTop: "1rem" }}>
              <div className="wpf-field">
                <FieldLabel>{t("wp_ehs_supervisor")}</FieldLabel>
                <NativeSelect value={form.ehsSupervisor} onChange={v => set("ehsSupervisor", v)}
                  options={users} placeholder={t("wp_select_user_placeholder")} />
              </div>
              <div className="wpf-field">
                <FieldLabel>{t("area")}</FieldLabel>
                <NativeSelect value={form.area} onChange={v => set("area", v)}
                  options={areas} placeholder={t("wp_select_placeholder")} />
              </div>
            </div>
          </Section>
        </div>

        {/* ── SECCIÓN 2: CONTRATISTA (condicional) ─────── */}
        {isContratista && (
          <div>
            <Section title="Datos del contratista"
              open={openSections.sec2} onToggle={() => toggleSection("sec2")}>

              {/* Razón social */}
              <div className="wpf-field">
                <FieldLabel required>Razón social</FieldLabel>
                <input className="wpf-input" maxLength={200} value={form.contratista.nombre}
                  placeholder="Nombre o razón social de la empresa contratista"
                  onChange={e => setContratista("nombre", e.target.value)} />
                <p className="wpf-char-count">{form.contratista.nombre.length}/200</p>
              </div>

              {/* Documento fiscal + Supervisor */}
              <div className="wpf-row-2" style={{ marginTop: "1rem" }}>
                <div className="wpf-field">
                  <FieldLabel required>Documento fiscal</FieldLabel>
                  <input className="wpf-input" value={form.contratista.doc}
                    placeholder={DOC_FISCAL_PLACEHOLDER[form.pais] || "Documento fiscal según país"}
                    onChange={e => setContratista("doc", e.target.value)} />
                </div>
                <div className="wpf-field">
                  <FieldLabel required>Supervisor del contratista</FieldLabel>
                  <input className="wpf-input" maxLength={150} value={form.contratista.supervisor}
                    placeholder="Nombre y teléfono del supervisor en obra"
                    onChange={e => setContratista("supervisor", e.target.value)} />
                  <p className="wpf-char-count">{form.contratista.supervisor.length}/150</p>
                </div>
              </div>

              {/* Documentación habilitante — checkboxes dinámicos por país */}
              <div className="wpf-field" style={{ marginTop: "1rem" }}>
                <FieldLabel required>
                  Documentación habilitante presentada
                  {form.pais && (
                    <span className="wpf-sub"> — {PAIS_OPTIONS.find(p => p.value === form.pais)?.label?.replace(/^\S+\s/, "")}</span>
                  )}
                </FieldLabel>
                {form.pais && PAIS_DOCS_ITEMS[form.pais] ? (
                  <div className="wpf-docs-grid">
                    {PAIS_DOCS_ITEMS[form.pais].map(doc => {
                      const selected  = form.contratista.docs.includes(doc);
                      const vencStr   = form.contratista.docsVencimiento?.[doc] || "";
                      const hoy       = new Date(); hoy.setHours(0, 0, 0, 0);
                      const isVencida = selected && vencStr && new Date(vencStr) < hoy;
                      return (
                        <div key={doc} className="wpf-doc-card-wrap">
                          <label className={`wpf-doc-card${selected ? " selected" : ""}${isVencida ? " vencida" : ""}`}>
                            <input type="checkbox"
                              checked={selected}
                              onChange={() => toggleContratistaDoc(doc)} />
                            <span>{doc}</span>
                          </label>
                          {selected && (
                            <div className="wpf-doc-venc">
                              <span className={`wpf-doc-venc__label${isVencida ? " wpf-doc-venc__label--warn" : ""}`}>
                                {isVencida ? "⚠ Vencida" : "Vencimiento"}
                              </span>
                              <input
                                type="date"
                                className={`wpf-doc-venc__input${isVencida ? " wpf-doc-venc__input--warn" : ""}`}
                                value={vencStr}
                                onChange={e => setDocVencimiento(doc, e.target.value)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="wpf-legal-hint wpf-legal-hint--placeholder">
                    Seleccioná un país en la Sección 1 para ver los documentos requeridos.
                  </p>
                )}
                {form.pais && PAIS_DOCS_ITEMS[form.pais] && form.contratista.docs.length === 0 && (
                  <p className="wpf-warn">Seleccioná al menos un documento habilitante.</p>
                )}
              </div>

            </Section>
          </div>
        )}

        {/* ── SECCIÓN 3: PERSONAL AUTORIZADO ───────────── */}
        <div>
          <Section title={t("wp_sec_personnel")}
            open={openSections.sec3} onToggle={() => toggleSection("sec3")}>

            {/* Tabla editable de trabajadores */}
            <datalist id="wpf-employees-list">
              {employees.map(e => (
                <option
                  key={e._id}
                  value={`${e.nombre || ""} ${e.apellido || ""}`.trim()}
                />
              ))}
            </datalist>

            <div className="wpf-personal-table">
              <div className="wpf-personal-table__head">
                <span>{t("wp_name_col")} <span className="wpf-req">*</span></span>
                <span>{t("wp_role_col")}</span>
                <span className="wpf-personal-table__badges-col">{t("wp_qualifications")}</span>
                <span />
              </div>

              {form.personal.map((p, idx) => (
                <div key={idx} className="wpf-personal-table__row">
                  <div className="wpf-personal-table__nombre-cell">
                    <input
                      className={`wpf-input${p.employee ? " wpf-input--linked" : ""}`}
                      list="wpf-employees-list"
                      placeholder={t("wp_search_employee_placeholder")}
                      value={p.nombre}
                      onChange={e => handlePersonalNombreChange(idx, e.target.value)}
                    />
                    {p.employee && (
                      <span className="wpf-personal-table__linked-badge" title="Vinculado al registro de empleados">
                        👤
                      </span>
                    )}
                  </div>
                  <input
                    className="wpf-input"
                    placeholder={t("wp_role_placeholder")}
                    value={p.cargo}
                    onChange={e => updatePersonal(idx, "cargo", e.target.value)}
                  />
                  <button
                    type="button"
                    className="wpf-personal-table__remove"
                    disabled={form.personal.length === 1}
                    onClick={() => removePersonal(idx)}
                    title={t("remove_row")}
                  >✕</button>
                </div>
              ))}
            </div>

            <button type="button" className="wpf-add-row-btn" onClick={addPersonal}>
              {t("wp_add_person")}
            </button>

            {form.personal.length === 0 && (
              <p className="wpf-warn">{t("wp_add_at_least_one_person")}</p>
            )}


            {/* Vigía */}
            <div className="wpf-field" style={{ marginTop: "1rem" }}>
              <FieldLabel required={requiereVigia}>{t("wp_vigia")}</FieldLabel>
              <input
                className="wpf-input"
                maxLength={100}
                placeholder={t("wp_vigia_placeholder")}
                value={form.vigia}
                onChange={e => set("vigia", e.target.value)}
              />
              {form.tipos.includes("espacio_confinado") && (
                <p className="wpf-vigia-norm">
                  🔴 <strong>Espacio confinado:</strong> rol vigía obligatorio — NR-33 · NOM-033-STPS-2015 · Res.SRT 295/03
                </p>
              )}
              {form.tipos.includes("caliente") && (
                <p className="wpf-vigia-norm">
                  🔴 <strong>Trabajo en caliente:</strong> fire-watch obligatorio durante toda la tarea y 30 min post-trabajo
                </p>
              )}
              {requiereVigia && !form.vigia.trim() && (
                <p className="wpf-warn">El vigía / fire-watch es obligatorio para este tipo de trabajo.</p>
              )}
            </div>

          </Section>
        </div>

        {/* ── SECCIÓN 4: EVALUACIÓN DE RIESGO / ATS-JSA ── */}
        <div>
          <Section title={t("wp_sec_risk")}
            open={openSections.sec4} onToggle={() => toggleSection("sec4")}>

            {/* ── 4A: Toolbox Talk ── */}
            <p className="wpf-ats-subsec-title">{t("wp_toolbox_subsec")}</p>

            <div className="wpf-row-3" style={{ marginTop: "1rem" }}>
              <div className="wpf-field">
                <FieldLabel required>{t("wp_toolbox_datetime")}</FieldLabel>
                <input type="datetime-local" className="wpf-input"
                  value={form.ats.toolboxFecha}
                  onChange={e => setAts("toolboxFecha", e.target.value)} />
              </div>
              <div className="wpf-field">
                <FieldLabel required>{t("wp_toolbox_conductor")}</FieldLabel>
                <input className="wpf-input" maxLength={200}
                  placeholder={t("wp_toolbox_conductor_placeholder")}
                  value={form.ats.toolboxResponsable}
                  onChange={e => setAts("toolboxResponsable", e.target.value)} />
              </div>
              <div className="wpf-field">
                <FieldLabel required>{t("wp_toolbox_duration")}</FieldLabel>
                <input type="number" className="wpf-input" min={10} max={480}
                  placeholder="≥ 10"
                  value={form.ats.toolboxDuracion}
                  onChange={e => setAts("toolboxDuracion", e.target.value)} />
                {form.ats.toolboxDuracion && Number(form.ats.toolboxDuracion) < 10 && (
                  <p className="wpf-warn">{t("wp_toolbox_min_duration_warn")}</p>
                )}
              </div>
            </div>

            <div className="wpf-field" style={{ marginTop: "1.25rem" }}>
              <FieldLabel required>{t("wp_toolbox_topics")} <span className="wpf-sub">({t("wp_toolbox_min_4")})</span></FieldLabel>
              <div className="wpf-toolbox-temas-grid">
                {TOOLBOX_TEMAS_OPTIONS.map(tema => (
                  <label key={tema}
                    className={`wpf-doc-card${form.ats.toolboxTemas.includes(tema) ? " selected" : ""}`}>
                    <input type="checkbox"
                      checked={form.ats.toolboxTemas.includes(tema)}
                      onChange={() => toggleToolboxTema(tema)} />
                    <span>{t(TOOLBOX_TEMAS_I18N[tema])}</span>
                  </label>
                ))}
              </div>
              {form.ats.toolboxTemas.length < 4 && (
                <p className="wpf-warn" style={{ marginTop: "0.4rem" }}>
                  {t("wp_toolbox_select_4")} {Math.max(0, 4 - form.ats.toolboxTemas.length)}.
                </p>
              )}
            </div>

            <div className="wpf-toolbox-asistentes">
              <p className="wpf-toolbox-asistentes__label">{t("wp_toolbox_attendees")}</p>
              <div className="wpf-toolbox-info-box">
                <p>{t("wp_toolbox_attendees_hint")}</p>
                {form.personal.length === 0 && (
                  <p className="wpf-toolbox-info-box__hint">{t("wp_toolbox_add_workers_first")}</p>
                )}
              </div>
            </div>

            <hr className="wpf-ats-divider" />

            {/* ── 4B: Modo ATS/JSA ── */}
            <p className="wpf-ats-subsec-title">{t("wp_ats_section_title")}</p>

            <div className="wpf-field">
              <FieldLabel required>{t("wp_modality_label")}</FieldLabel>
              <div className="wpf-modo-cards">
                {[
                  { value: "jsa",       icon: "📋", label: t("wp_mode_jsa_label"),       desc: t("wp_mode_jsa_desc") },
                  { value: "vinculado", icon: "🔗", label: t("wp_mode_linked_label"),    desc: t("wp_mode_linked_desc") },
                  { value: "externo",   icon: "📎", label: t("wp_mode_external_label"),  desc: t("wp_mode_external_desc") },
                ].map(m => (
                  <label key={m.value}
                    className={`wpf-modo-card${form.ats.modo === m.value ? " selected" : ""}`}>
                    <input type="radio" name="ats-modo" value={m.value}
                      checked={form.ats.modo === m.value}
                      onChange={() => setAts("modo", m.value)} />
                    <span className="wpf-modo-card__icon">{m.icon}</span>
                    <span className="wpf-modo-card__label">{m.label}</span>
                    <span className="wpf-modo-card__desc">{m.desc}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* EvR vinculado */}
            {form.ats.modo === "vinculado" && (
              <div className="wpf-field" style={{ marginTop: "1rem" }}>
                <FieldLabel required>{t("wp_evr_linked_label")}</FieldLabel>
                {!form.site ? (
                  <p className="wpf-legal-hint wpf-legal-hint--warn">{t("wp_select_site_first")}</p>
                ) : evrsLoading ? (
                  <p className="wpf-legal-hint">{t("wp_loading_evr")}</p>
                ) : evrs.length === 0 ? (
                  <p className="wpf-legal-hint wpf-legal-hint--warn">{t("wp_no_evr_for_site")}</p>
                ) : (
                  <select
                    className="wpf-select"
                    value={form.ats.vinculo}
                    onChange={e => {
                      const sel = evrs.find(r => r._id === e.target.value);
                      setAts("vinculo", e.target.value);
                      setAts("vinculoLabel", sel
                        ? `EvR-${sel.customId || "??"}: ${sel.hazardDescription || sel.riskType || t("wp_no_description")}`
                        : "");
                      setCondicionesChanged(false);
                    }}
                  >
                    <option value="">— {t("wp_select_evr_ph")} —</option>
                    {evrs.map(r => {
                      const vencida = r.nextReassessment && new Date(r.nextReassessment) < new Date();
                      return (
                        <option key={r._id} value={r._id}>
                          {vencida ? "⚠ " : ""}
                          {r.customId ? `EvR-${r.customId} · ` : ""}
                          {r.hazardDescription || r.riskType || r._id}
                          {r.area ? ` (${r.area})` : ""}
                          {vencida ? ` — ${t("wp_expired_label")}` : ""}
                        </option>
                      );
                    })}
                  </select>
                )}

                {form.ats.vinculo && form.ats.vinculoLabel && (
                  <>
                    <p className="wpf-legal-hint">
                      ✓ Vinculado: <strong>{form.ats.vinculoLabel}</strong>
                      {selectedEvr?.nextReassessment && (
                        <span className={`wpf-evr-date${evrVencida ? " wpf-evr-date--vencida" : ""}`}>
                          {" · "}Vigente hasta: {new Date(selectedEvr.nextReassessment).toLocaleDateString("es-AR")}
                          {evrVencida && " ⚠ Vencida"}
                        </span>
                      )}
                    </p>

                    <label className="wpf-evr-condiciones">
                      <input
                        type="checkbox"
                        checked={condicionesChanged}
                        onChange={e => setCondicionesChanged(e.target.checked)}
                      />
                      {t("wp_conditions_changed")}
                    </label>

                    {showJsaSuggestion && (
                      <div className="wpf-evr-warn">
                        <strong>
                          {evrVencida ? `⚠ ${t("wp_evr_expired_title")}` : `⚠ ${t("wp_conditions_modified_title")}`}
                        </strong>
                        <p>
                          {evrVencida
                            ? `${t("wp_evr_expired_text")} ${new Date(selectedEvr.nextReassessment).toLocaleDateString()}.`
                            : t("wp_conditions_differ")}
                          {" "}{t("wp_jsa_recommendation")}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Tabla JSA */}
            {form.ats.modo === "jsa" && (
              <div style={{ marginTop: "1rem" }}>
                <FieldLabel required>{t("wp_jsa_steps_label")}</FieldLabel>
                <div className="wpf-jsa-wrap">
                  <table className="wpf-jsa-table">
                    <thead>
                      <tr>
                        <th rowSpan={2}>#</th>
                        <th rowSpan={2}>{t("wp_jsa_col_step")}</th>
                        <th rowSpan={2}>{t("wp_jsa_col_category")}</th>
                        <th rowSpan={2}>{t("wp_jsa_col_hazard")}</th>
                        <th colSpan={3} className="wpf-jsa-group-header wpf-jsa-group--in">{t("wp_jsa_col_inherent")}</th>
                        <th rowSpan={2}>{t("wp_jsa_col_hierarchy")}</th>
                        <th rowSpan={2}>{t("wp_jsa_col_controls")}</th>
                        <th colSpan={3} className="wpf-jsa-group-header wpf-jsa-group--res">{t("wp_jsa_col_residual")}</th>
                        <th rowSpan={2}>{t("wp_jsa_col_responsible")}</th>
                        <th rowSpan={2}></th>
                      </tr>
                      <tr>
                        <th className="wpf-jsa-group--in">{t("wp_jsa_sev")}</th>
                        <th className="wpf-jsa-group--in">{t("wp_jsa_prob")}</th>
                        <th className="wpf-jsa-group--in">{t("wp_jsa_value")}</th>
                        <th className="wpf-jsa-group--res">{t("wp_jsa_sev")}</th>
                        <th className="wpf-jsa-group--res">{t("wp_jsa_prob")}</th>
                        <th className="wpf-jsa-group--res">{t("wp_jsa_value")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.ats.pasos.map((p, idx) => {
                        const nrIn  = nivelRiesgo(p.sevIn,  p.probIn);
                        const nrRes = nivelRiesgo(p.sevRes, p.probRes);
                        return (
                          <tr key={idx}>
                            <td className="wpf-jsa-table__num">{idx + 1}</td>
                            <td className="wpf-jsa-td--wide">
                              <textarea className="wpf-textarea wpf-textarea--sm" rows={2}
                                placeholder={t("wp_jsa_step_ph")}
                                value={p.paso}
                                onChange={e => updatePaso(idx, "paso", e.target.value)} />
                            </td>
                            <td>
                              <select className="wpf-select"
                                value={p.categoria}
                                onChange={e => updatePaso(idx, "categoria", e.target.value)}>
                                <option value="">— {t("wp_jsa_cat_ph")} —</option>
                                {CATEGORIA_OPTIONS.map(o => (
                                  <option key={o.value} value={o.value}>{t(o.value)}</option>
                                ))}
                              </select>
                            </td>
                            <td className="wpf-jsa-td--wide">
                              <textarea className="wpf-textarea wpf-textarea--sm" rows={2}
                                placeholder={t("wp_jsa_hazard_ph")}
                                value={p.peligro}
                                onChange={e => updatePaso(idx, "peligro", e.target.value)} />
                            </td>
                            {/* Inherente */}
                            <td className="wpf-jsa-group--in">
                              <select className="wpf-select wpf-select--sm"
                                value={p.sevIn}
                                onChange={e => updatePaso(idx, "sevIn", Number(e.target.value))}>
                                {[1,2,3,4,5].map(v => <option key={v} value={v}>{v}</option>)}
                              </select>
                            </td>
                            <td className="wpf-jsa-group--in">
                              <select className="wpf-select wpf-select--sm"
                                value={p.probIn}
                                onChange={e => updatePaso(idx, "probIn", Number(e.target.value))}>
                                {[1,2,3,4,5].map(v => <option key={v} value={v}>{v}</option>)}
                              </select>
                            </td>
                            <td className="wpf-jsa-group--in">
                              <span className={`wpf-nr-badge ${nrIn.cls}`}>{nrIn.n}</span>
                            </td>
                            {/* Controles */}
                            <td>
                              <select className="wpf-select"
                                value={p.jerarquia}
                                onChange={e => updatePaso(idx, "jerarquia", e.target.value)}>
                                <option value="">— {t("wp_jsa_level_ph")} —</option>
                                {JERARQUIA_OPTIONS.map(o => (
                                  <option key={o.value} value={o.value}>{t(o.value)}</option>
                                ))}
                              </select>
                            </td>
                            <td className="wpf-jsa-td--wide">
                              <textarea className="wpf-textarea wpf-textarea--sm" rows={2}
                                placeholder={t("wp_jsa_controls_ph")}
                                value={p.controles}
                                onChange={e => updatePaso(idx, "controles", e.target.value)} />
                            </td>
                            {/* Residual */}
                            <td className="wpf-jsa-group--res">
                              <select className="wpf-select wpf-select--sm"
                                value={p.sevRes}
                                onChange={e => updatePaso(idx, "sevRes", Number(e.target.value))}>
                                {[1,2,3,4,5].map(v => <option key={v} value={v}>{v}</option>)}
                              </select>
                            </td>
                            <td className="wpf-jsa-group--res">
                              <select className="wpf-select wpf-select--sm"
                                value={p.probRes}
                                onChange={e => updatePaso(idx, "probRes", Number(e.target.value))}>
                                {[1,2,3,4,5].map(v => <option key={v} value={v}>{v}</option>)}
                              </select>
                            </td>
                            <td className="wpf-jsa-group--res">
                              <span className={`wpf-nr-badge ${nrRes.cls}`}>{nrRes.n}</span>
                            </td>
                            <td>
                              <input className="wpf-input" placeholder={t("wp_jsa_responsible_ph")}
                                value={p.responsable}
                                onChange={e => updatePaso(idx, "responsable", e.target.value)} />
                            </td>
                            <td>
                              <button type="button" className="wpf-personal-table__remove"
                                disabled={form.ats.pasos.length === 1}
                                onClick={() => removePaso(idx)}>✕</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button type="button" className="wpf-add-row-btn" style={{ marginTop: "0.4rem" }}
                  onClick={addPaso}>{t("wp_jsa_add_step")}</button>
                <p className="wpf-legal-hint" style={{ marginTop: "0.4rem" }}>
                  {t("wp_jsa_hierarchy_hint")}
                </p>
              </div>
            )}

            {/* ATS externo */}
            {form.ats.modo === "externo" && (
              <div style={{ marginTop: "1rem" }}>
                <div className="wpf-row-2">
                  <div className="wpf-field">
                    <FieldLabel required>{t("wp_ats_ext_responsible")}</FieldLabel>
                    <input className="wpf-input" maxLength={200}
                      placeholder={t("wp_ats_ext_responsible_ph")}
                      value={form.ats.externoQuien}
                      onChange={e => setAts("externoQuien", e.target.value)} />
                    <p className="wpf-char-count">{form.ats.externoQuien.length}/200</p>
                  </div>
                  <div className="wpf-field">
                    <FieldLabel>{t("wp_ats_ext_date")}</FieldLabel>
                    <input type="date" className="wpf-input"
                      value={form.ats.externoFecha}
                      onChange={e => setAts("externoFecha", e.target.value)} />
                  </div>
                </div>
                <div className="wpf-field" style={{ marginTop: "1rem" }}>
                  <FieldLabel required>
                    Archivo ATS firmado <span className="wpf-sub">(PDF · JPG · PNG · máx 10 MB)</span>
                  </FieldLabel>
                  {form.ats.archivoNombre ? (
                    <div className="wpf-ats-file-existing">
                      <span>📎 {form.ats.archivoNombre}</span>
                      <button type="button" className="wpf-personal-table__remove"
                        onClick={() => { setAts("archivoNombre", ""); setAtsFile(null); }}>✕</button>
                    </div>
                  ) : (
                    <>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="wpf-input"
                        onChange={e => {
                          const f = e.target.files?.[0] || null;
                          if (f && f.size > 10 * 1024 * 1024) {
                            toast.warning("El archivo supera los 10 MB. Elegí un archivo más pequeño.");
                            e.target.value = "";
                            return;
                          }
                          setAtsFile(f);
                        }} />
                      {atsFile && (
                        <p className="wpf-legal-hint">
                          📎 {atsFile.name} — {(atsFile.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      )}
                    </>
                  )}
                  {!form.ats.archivoNombre && !atsFile && (
                    <p className="wpf-warn">Adjuntá el documento ATS firmado.</p>
                  )}
                </div>
              </div>
            )}

            <hr className="wpf-ats-divider" />

            {/* ── 4C: Matriz de riesgo 5×5 ── */}
            {form.ats.modo === "jsa" && form.ats.pasos.some(p => p.peligro) && (
              <>
                <p className="wpf-ats-subsec-title">4C — Matriz de riesgo 5×5</p>
                <div className="wpf-rm-pair">
                  {[
                    { label: "Riesgo inherente (antes de controles)", cells: riskMatrixCells.inherente },
                    { label: "Riesgo residual (después de controles)", cells: riskMatrixCells.residual },
                  ].map(({ label, cells }) => (
                    <div key={label} className="wpf-rm-container">
                      <p className="wpf-rm-sublabel">{label}</p>
                      <div className="wpf-rm-ylabel">↑ Probabilidad</div>
                      <div className="wpf-rm-grid">
                        {[5,4,3,2,1].map(prob => (
                          <React.Fragment key={prob}>
                            <div className="wpf-rm-axis-label">{prob}</div>
                            {[1,2,3,4,5].map(sev => {
                              const nr    = nivelRiesgo(prob, sev);
                              const count = cells[`${prob}-${sev}`] || 0;
                              return (
                                <div key={sev} className={`wpf-rm-cell ${nr.cls}${count ? " active" : ""}`}>
                                  <span className="wpf-rm-cell__val">{prob * sev}</span>
                                  {count > 0 && <span className="wpf-rm-cell__dot">{count}</span>}
                                </div>
                              );
                            })}
                          </React.Fragment>
                        ))}
                        <div className="wpf-rm-corner" />
                        {[1,2,3,4,5].map(s => (
                          <div key={s} className="wpf-rm-axis-label">{s}</div>
                        ))}
                      </div>
                      <div className="wpf-rm-xlabel">Severidad →</div>
                    </div>
                  ))}
                </div>
                <div className="wpf-rm-legend">
                  <span className="wpf-nr-badge nr-low">1–4 Bajo</span>
                  <span className="wpf-nr-badge nr-mod">5–9 Moderado</span>
                  <span className="wpf-nr-badge nr-high">10–14 Alto</span>
                  <span className="wpf-nr-badge nr-crit">15–25 Crítico</span>
                </div>
                <p className="wpf-legal-hint" style={{ marginTop: "0.3rem" }}>
                  ● Número en celda = pasos del JSA en ese cuadrante.
                </p>
                {maxRiesgoResidual >= 15 && (
                  <div className="wpf-residual-warn">
                    ⚠️ <strong>Riesgo residual extremo (≥ 15):</strong> requiere aprobación adicional de Gerente del Sitio antes de iniciar el trabajo.
                  </div>
                )}
              </>
            )}

            {/* ── 4D: Stop Work Authority ── */}
            <div className="wpf-swa-banner">
              <strong>🛑 Stop Work Authority</strong>
              <p>
                Cualquier persona que detecte una condición insegura durante la ejecución está
                {" "}<strong>autorizada y obligada</strong> a detener el trabajo de inmediato,
                sin riesgo de represalia.
              </p>
              <p className="wpf-swa-banner__refs">Ref.: IOGP 577</p>
            </div>

          </Section>
        </div>

        {/* ── SECCIÓN 5: EPP REQUERIDO ──────────────────── */}
        <div>
          <Section title={t("wp_sec_epp")}
            open={openSections.sec5} onToggle={() => toggleSection("sec5")}>

            {eppSugeridos.size > 0 && (
              <p className="wpf-legal-hint wpf-legal-hint--info" style={{ marginBottom: "0.8rem" }}>
                💡 {t("epp_rl_suggested_hint")}
              </p>
            )}

            {/* Checkboxes por región */}
            <FieldLabel required>{t("epp_rl_required")}</FieldLabel>
            <div className="wpf-epp-regions">
              {Object.entries(EPP_CATALOG).map(([region, items]) => {
                const meta     = EPP_REGION_META[region] || { icon: "🔧", normativas: "" };
                const selected = items.filter(item => form.epp.includes(`${region}:${item}`)).length;
                return (
                  <div key={region} className="wpf-epp-region">
                    <div className="wpf-epp-region__title">
                      <span className="wpf-epp-region__title-left">
                        <span>{meta.icon}</span>
                        <span>{t(EPP_REGION_I18N[region]) || region}</span>
                        {meta.normativas && (
                          <span className="wpf-epp-region__normativas">{meta.normativas}</span>
                        )}
                      </span>
                      <span className="wpf-epp-region__count">{selected} / {items.length} {t("epp_rl_selected")}</span>
                    </div>
                    <div className="wpf-epp-region__items">
                      {items.map(item => {
                        const val      = `${region}:${item}`;
                        const checked  = form.epp.includes(val);
                        const sugerido = eppSugeridos.has(val);
                        const i18n     = EPP_ITEM_I18N[item];
                        return (
                          <label key={val}
                            className={`wpf-epp-item${checked ? " wpf-epp-item--checked" : ""}${sugerido ? " wpf-epp-item--suggested" : ""}`}>
                            <input type="checkbox" checked={checked}
                              onChange={() => toggleEpp(val)} />
                            <div className="wpf-epp-item__content">
                              <span className="wpf-epp-item__name">{i18n ? t(i18n.n) : item}</span>
                              {i18n && <span className="wpf-epp-item__desc">{t(i18n.d)}</span>}
                              {sugerido && <span className="wpf-epp-item__badge">{t("epp_rl_suggested_badge")}</span>}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {form.epp.length > 0 && (
              <p className="wpf-legal-hint" style={{ marginTop: "0.4rem" }}>
                {form.epp.length} EPP{form.epp.length > 1 ? "s" : ""} {t("epp_rl_selected")}.
              </p>
            )}

            {/* Stock por EPP — verificación automática contra módulo EPP */}
            {form.epp.length > 0 && (
              <div className="wpf-epp-stock-section">
                <p className="wpf-ats-subsec-title" style={{ marginBottom: "0.5rem" }}>
                  {t("epp_rl_stock_title")}
                  {eppStockLoading && <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: "0.5rem", fontSize: "0.8rem" }}>{t("epp_rl_stock_checking")}</span>}
                </p>
                <div className="wpf-epp-stock-list">
                  {form.epp.map(val => {
                    const name   = val.includes(":") ? val.split(":").slice(1).join(":") : val;
                    const i18n   = EPP_ITEM_I18N[name];
                    const status = eppStock[name];
                    return (
                      <div key={val} className="wpf-epp-stock-row">
                        <span className="wpf-epp-stock-row__name">{i18n ? t(i18n.n) : name}</span>
                        {eppStockLoading ? (
                          <span className="wpf-epp-stock-badge wpf-epp-stock-badge--pending">…</span>
                        ) : status === "ok" ? (
                          <span className="wpf-epp-stock-badge wpf-epp-stock-badge--ok">{t("epp_rl_stock_ok")}</span>
                        ) : status === "falta" ? (
                          <span className="wpf-epp-stock-badge wpf-epp-stock-badge--falta">{t("epp_rl_stock_out")}</span>
                        ) : (
                          <span className="wpf-epp-stock-badge wpf-epp-stock-badge--nr">{t("epp_rl_stock_no_record")}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {Object.values(eppStock).some(s => s === "falta") && (
                  <p className="wpf-warn" style={{ marginTop: "0.4rem" }}>
                    {t("epp_rl_stock_warn")}
                  </p>
                )}
                {!form.site && (
                  <p className="wpf-legal-hint" style={{ marginTop: "0.4rem" }}>
                    {t("epp_rl_stock_select_site")}
                  </p>
                )}
              </div>
            )}

            <hr className="wpf-ats-divider" />

            {/* Verificación pre-uso */}
            <div className="wpf-epp-preuso">
              <FieldLabel required>{t("epp_rl_preuso_label")}</FieldLabel>
              <p className="wpf-legal-hint" style={{ marginBottom: "0.6rem" }}>
                {t("epp_rl_preuso_desc")}
              </p>
              <div className="wpf-epp-preuso__toggle">
                <button type="button"
                  className={`wpf-epp-preuso__btn${form.eppPreUso ? " wpf-epp-preuso__btn--yes" : ""}`}
                  onClick={() => set("eppPreUso", true)}>
                  {t("epp_rl_preuso_yes")}
                </button>
                <button type="button"
                  className={`wpf-epp-preuso__btn${!form.eppPreUso ? " wpf-epp-preuso__btn--no" : ""}`}
                  onClick={() => set("eppPreUso", false)}>
                  {t("epp_rl_preuso_no")}
                </button>
              </div>
              {!form.eppPreUso && (
                <p className="wpf-warn" style={{ marginTop: "0.4rem" }}>
                  {t("epp_rl_preuso_warn")}
                </p>
              )}
            </div>

          </Section>
        </div>

        {/* ── SECCIÓN 6: LMRA ──────────────────────────────── */}
        <div>
          <Section title={t("wp_sec_lmra")}
            open={openSections.sec6} onToggle={() => toggleSection("sec6")}>

            {lmraBlocked ? (
              <div className="wpf-lmra-blocked">
                <strong>{t("lmra_blocked_title")}</strong>
                <p>
                  {t("lmra_blocked_pre")} <strong>{t("lmra_blocked_section")}</strong> {t("lmra_blocked_post")}
                </p>
              </div>
            ) : (
              <>
                <div className="wpf-lmra-info">
                  📱 <strong>{t("lmra_info_onsite")}</strong> — {t("lmra_info_desc")}{" "}
                  <strong>{t("lmra_info_no_block")}</strong>, {t("lmra_info_mandatory")} <strong>{t("lmra_info_in_execution")}</strong>.
                  Ref.: Shell HSSE &amp; SP Control Framework.
                </div>

                {lmraSaved && (
                  <div className="wpf-lmra-info">
                    <strong>{t("lmra_saved_title")}</strong> — {t("lmra_saved_msg")}
                  </div>
                )}

                {/* 6.1 Fecha */}
                <div className="wpf-field" style={{ maxWidth: "280px", marginTop: "1rem" }}>
                  <FieldLabel>{t("lmra_eval_datetime")}</FieldLabel>
                  <input type="datetime-local" className="wpf-input"
                    value={form.lmra.fecha}
                    disabled={lmraSaved}
                    onChange={e => setLmra("fecha", e.target.value)} />
                </div>

                {/* 6.2–6.5 Preguntas */}
                <div className="wpf-lmra-preguntas">
                  {LMRA_PREGUNTAS.map((_, idx) => {
                    const p    = form.lmra.preguntas[idx];
                    const esNo = p.respuesta === false;
                    const esSi = p.respuesta === true;
                    return (
                      <div key={idx}
                        className={`wpf-lmra-card${esSi ? " wpf-lmra-card--si" : ""}${esNo ? " wpf-lmra-card--no" : ""}`}>
                        <div className="wpf-lmra-card__header">
                          <span className="wpf-lmra-card__num">{idx + 1}</span>
                          <span className="wpf-lmra-card__pregunta">{t(`lmra_q${idx + 1}`)}</span>
                          <div className="wpf-lmra-card__toggle">
                            <button type="button" disabled={lmraSaved}
                              className={`wpf-lmra-toggle-btn${esSi ? " wpf-lmra-toggle-btn--si" : ""}`}
                              onClick={() => updateLmraPregunta(idx, "respuesta", true)}>
                              {t("lmra_yes")}
                            </button>
                            <button type="button" disabled={lmraSaved}
                              className={`wpf-lmra-toggle-btn${esNo ? " wpf-lmra-toggle-btn--no" : ""}`}
                              onClick={() => updateLmraPregunta(idx, "respuesta", false)}>
                              {t("lmra_no")}
                            </button>
                          </div>
                        </div>
                        <textarea
                          className="wpf-textarea wpf-textarea--sm"
                          rows={2}
                          maxLength={300}
                          disabled={lmraSaved}
                          placeholder={t("lmra_observation_placeholder")}
                          value={p.observacion}
                          onChange={e => updateLmraPregunta(idx, "observacion", e.target.value)} />
                        {p.observacion.length > 0 && (
                          <p className="wpf-char-count">{p.observacion.length}/300</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Banner "No" */}
                {lmraTieneNo && (
                  <div className="wpf-lmra-no-warn">
                    <strong>{t("lmra_blocked_work_title")}</strong>
                    <p>{t("lmra_blocked_work_msg")}</p>
                    <ul>
                      <li>{t("lmra_action_1")}</li>
                      <li>{t("lmra_action_2")}</li>
                      <li>{t("lmra_action_3")}</li>
                    </ul>
                  </div>
                )}

                {/* Banner "todo OK" */}
                {lmraCompleta && !lmraTieneNo && (
                  <div className="wpf-lmra-ok">
                    {t("lmra_all_ok")}
                  </div>
                )}
              </>
            )}

          </Section>
        </div>

        {/* ── SECCIÓN 7: FLUJO DE APROBACIÓN ──────────────── */}
        <div>
          <Section title={t("wp_sec_approval")}
            open={openSections.sec7} onToggle={() => toggleSection("sec7")}>

            {/* Barra de acciones del editor */}
            <div className="wpf-flujo-bar">
              <span className="wpf-flujo-bar__info">
                {form.flujoAprobacion.length} {form.flujoAprobacion.length !== 1 ? t("flujo_nivel_p") : t("flujo_nivel_s")}
                {" · "}
                {form.flujoAprobacion.filter(n => n.activo).length} {form.flujoAprobacion.filter(n => n.activo).length !== 1 ? t("flujo_activo_p") : t("flujo_activo_s")}
              </span>
              <div className="wpf-flujo-bar__actions">
                <button type="button" className="wpf-flujo-bar__btn"
                  disabled={form.flujoAprobacion.length >= 7}
                  onClick={agregarNivelAprobacion}>
                  {t("flujo_add_approver")}
                </button>
                <button type="button" className="wpf-flujo-bar__btn wpf-flujo-bar__btn--ghost"
                  onClick={reiniciarFlujoDesdeTemplate}>
                  {t("flujo_reset_template")}
                </button>
              </div>
            </div>

            {/* Lista de niveles */}
            <div className="wpf-flujo-lista">
              {form.flujoAprobacion.map((nivel, idx) => {
                const isFirst = idx === 0;
                const isLast  = idx === form.flujoAprobacion.length - 1;
                const fijo    = isFirst || isLast;
                return (
                  <div key={idx}
                    className={`wpf-flujo-nivel${!nivel.activo ? " wpf-flujo-nivel--inactivo" : ""}${fijo ? " wpf-flujo-nivel--fijo" : ""}`}>

                    {/* Cabecera del nivel */}
                    <div className="wpf-flujo-nivel__head">
                      <span className="wpf-flujo-nivel__num">{idx + 1}</span>
                      <input className="wpf-input wpf-flujo-nivel__name-input"
                        value={nivel.nivel}
                        disabled={fijo}
                        onChange={e => updateFlujoNivel(idx, "nivel", e.target.value)} />
                      <div className="wpf-flujo-nivel__badges">
                        {fijo && <span className="wpf-flujo-badge wpf-flujo-badge--fijo">{t("flujo_obligatorio")}</span>}
                        {nivel.requeridoPorTipo && <span className="wpf-flujo-badge wpf-flujo-badge--auto">{t("flujo_auto_badge")}</span>}
                      </div>
                      <div className="wpf-flujo-nivel__ctrl">
                        {!fijo && (
                          <label className="wpf-flujo-active-toggle"
                            title={nivel.activo ? t("flujo_deactivate_tip") : t("flujo_activate_tip")}>
                            <input type="checkbox" checked={nivel.activo}
                              onChange={e => updateFlujoNivel(idx, "activo", e.target.checked)} />
                            <span>{nivel.activo ? t("flujo_activo") : t("flujo_inactivo")}</span>
                          </label>
                        )}
                        {!fijo && !nivel.requeridoPorTipo && form.flujoAprobacion.length > 2 && (
                          <button type="button" className="wpf-personal-table__remove"
                            title={t("flujo_delete_level")}
                            onClick={() => eliminarNivelAprobacion(idx)}>✕</button>
                        )}
                      </div>
                    </div>

                    {/* Cuerpo — solo si activo */}
                    {nivel.activo ? (
                      <div className="wpf-flujo-nivel__body">
                        <div className="wpf-row-2">
                          <div className="wpf-field">
                            <FieldLabel required>{t("flujo_rol_label")}</FieldLabel>
                            <input className="wpf-input" maxLength={100}
                              placeholder={t("flujo_rol_placeholder")}
                              value={nivel.rol}
                              disabled={isFirst}
                              onChange={e => updateFlujoNivel(idx, "rol", e.target.value)} />
                          </div>
                          <div className="wpf-field">
                            <FieldLabel required>{t("flujo_criterio_label")}</FieldLabel>
                            <select className="wpf-select"
                              value={nivel.criterio}
                              disabled={isFirst}
                              onChange={e => updateFlujoNivel(idx, "criterio", e.target.value)}>
                              {CRITERIO_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{t("flujo_criterio_" + o.value)}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Titular dinámico */}
                        <div className="wpf-field">
                          <FieldLabel required={nivel.criterio === "usuario"}>{t("flujo_titular_label")}</FieldLabel>
                          {(nivel.criterio === "auto") && (
                            <input className="wpf-input" disabled value={t("flujo_titular_auto")} />
                          )}
                          {(nivel.criterio === "jerarquia") && (
                            <input className="wpf-input" disabled value={t("flujo_titular_jerarquia")} />
                          )}
                          {(nivel.criterio === "usuario") && (
                            <select className="wpf-select"
                              value={nivel.titular}
                              onChange={e => updateFlujoNivel(idx, "titular", e.target.value)}>
                              <option value="">{t("flujo_select_user")}</option>
                              {users.map(u => <option key={u.value} value={u.label}>{u.label}</option>)}
                            </select>
                          )}
                          {(nivel.criterio === "rol") && (
                            <select className="wpf-select"
                              value={nivel.titular}
                              onChange={e => updateFlujoNivel(idx, "titular", e.target.value)}>
                              <option value="">{t("flujo_select_rol")}</option>
                              {ROL_OPCIONES.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          )}
                        </div>

                        {/* Suplentes — chips + select */}
                        <div className="wpf-field">
                          <FieldLabel>{t("flujo_suplentes_label")} <span className="wpf-sub">({t("flujo_suplentes_max")})</span></FieldLabel>
                          <div className="wpf-flujo-suplentes">
                            {nivel.suplentes.map(s => (
                              <span key={s} className="wpf-flujo-chip">
                                {s}
                                <button type="button" className="wpf-flujo-chip__remove"
                                  onClick={() => removeSuplente(idx, s)}>×</button>
                              </span>
                            ))}
                            {nivel.suplentes.length < 3 && (
                              <select className="wpf-select wpf-select--inline"
                                value={suplenteDraft[idx] || ""}
                                onChange={e => {
                                  const val = e.target.value;
                                  if (!val) return;
                                  addSuplente(idx, val);
                                  setSuplenteDraft(p => ({ ...p, [idx]: "" }));
                                }}>
                                <option value="">{t("flujo_add_suplente")}</option>
                                {users
                                  .filter(u => u.label !== nivel.titular && !nivel.suplentes.includes(u.label))
                                  .map(u => <option key={u.value} value={u.label}>{u.label}</option>)}
                              </select>
                            )}
                          </div>
                        </div>

                        {/* Acciones permitidas */}
                        <div className="wpf-field">
                          <FieldLabel required>{t("flujo_acciones_label")}</FieldLabel>
                          <div className="wpf-flujo-acciones">
                            {ACCIONES_OPTIONS.map(a => (
                              <label key={a.value}
                                className={`wpf-flujo-accion${nivel.acciones.includes(a.value) ? " wpf-flujo-accion--checked" : ""}`}>
                                <input type="checkbox"
                                  checked={nivel.acciones.includes(a.value)}
                                  onChange={() => toggleFlujoAccion(idx, a.value)} />
                                {t("flujo_accion_" + a.value)}
                              </label>
                            ))}
                          </div>
                          {nivel.acciones.length === 0 && (
                            <p className="wpf-warn">{t("flujo_accion_warn")}</p>
                          )}
                        </div>

                        {/* Opciones adicionales */}
                        <div className="wpf-flujo-opts">
                          <label className="wpf-flujo-opt-check">
                            <input type="checkbox"
                              checked={nivel.firmaDigital}
                              onChange={e => updateFlujoNivel(idx, "firmaDigital", e.target.checked)} />
                            {t("flujo_firma_digital")}
                          </label>
                          <label className="wpf-flujo-opt-check">
                            <input type="checkbox"
                              checked={nivel.comentarioObligatorio}
                              onChange={e => updateFlujoNivel(idx, "comentarioObligatorio", e.target.checked)} />
                            {t("flujo_comentario_obligatorio")}
                          </label>
                        </div>
                      </div>
                    ) : (
                      <p className="wpf-flujo-nivel__desactivado">
                        {t("flujo_nivel_desactivado")}
                      </p>
                    )}

                  </div>
                );
              })}
            </div>

            {/* Reglas automáticas detectadas */}
            {flujoReglas.length > 0 && (
              <div className="wpf-flujo-reglas">
                <p className="wpf-flujo-reglas__title">{t("flujo_auto_reglas_title")}</p>
                <ul className="wpf-flujo-reglas__list">
                  {flujoReglas.map(r => (
                    <li key={r.id}><strong>{r.id}:</strong> {r.msg}</li>
                  ))}
                </ul>
                <p className="wpf-legal-hint" style={{ marginTop: "0.4rem" }}>
                  {t("flujo_add_approver_hint")}
                </p>
              </div>
            )}

            {/* Escalamiento — solo lectura desde Config Global */}
            <div className="wpf-flujo-escalamiento">
              <p className="wpf-ats-subsec-title" style={{ marginBottom: "0.6rem" }}>
                {t("flujo_escalamiento_title")} <span className="wpf-sub">{t("flujo_escalamiento_config")}</span>
              </p>
              <div className="wpf-flujo-esc-row">
                <div className="wpf-flujo-esc-field">
                  <span className="wpf-flujo-esc-label">{t("flujo_timeout")}</span>
                  <span className="wpf-flujo-esc-val">{approvalCfg?.escalamiento?.timeoutMinutos ?? 120} {t("flujo_min")}</span>
                </div>
                <div className="wpf-flujo-esc-field">
                  <span className="wpf-flujo-esc-label">{t("flujo_reminder")}</span>
                  <span className="wpf-flujo-esc-val">{t("flujo_every")} {approvalCfg?.escalamiento?.recordatorioMinutos ?? 30} {t("flujo_min")}</span>
                </div>
                <div className="wpf-flujo-esc-field">
                  <span className="wpf-flujo-esc-label">{t("flujo_escalate_to")}</span>
                  <span className="wpf-flujo-esc-val">{t("flujo_escalar_" + (approvalCfg?.escalamiento?.escalarA ?? "jefe_directo"))}</span>
                </div>
              </div>
              <p className="wpf-legal-hint wpf-legal-hint--warn" style={{ marginTop: "0.5rem" }}>
                {t("flujo_edit_hint")}
              </p>
            </div>

          </Section>
        </div>

      </div>{/* /wpf__scroll */}

      {/* ── Acciones ─────────────────────────────────── */}
      <div className="wpf-actions">
        <div className="wpf-actions__validation">
          {validationErrors.length > 0 && (
            <span className="wpf-actions__validation-text">
              {t("val_label")} <span className="wpf-actions__validation-icon">&#9651;</span>
              {" "}{t("val_missing")} {validationErrors.map(k => t(k)).join(", ")}
            </span>
          )}
        </div>
        <div className="wpf-actions__buttons">
          {form.status === "draft" ? (
            <>
              <button
                className="wpf-actions__btn"
                type="button"
                onClick={handleSaveDraft}
                disabled={saving}
              >
                <Icon name="save" />{saving ? t("saving") : t("save_draft")}
              </button>
              <button
                className="wpf-actions__btn wpf-actions__btn--primary"
                type="button"
                onClick={handleSendForApproval}
                disabled={saving || validationErrors.length > 0}
              >
                <Icon name="send" />{saving ? t("sending") : t("send_for_approval")}
              </button>
            </>
          ) : (
            <button
              className="wpf-actions__btn wpf-actions__btn--primary"
              type="button"
              onClick={handleSaveChanges}
              disabled={saving}
            >
              {saving ? t("saving") : t("save_changes")}
            </button>
          )}
          <button className="wpf-actions__cancel-btn" type="button" onClick={close} disabled={saving}>
            {t("cancel")}
          </button>
        </div>
      </div>

      {/* ── Modales de alta rápida (Sitio / Sector) ── */}
      <BasicModal show={showSiteModal} close={() => setShowSiteModal(false)} title="Nuevo sitio" size="small">
        <SiteForm
          close={() => setShowSiteModal(false)}
          onReload={() => { setShowSiteModal(false); loadSites(); }}
          company={user?.company}
        />
      </BasicModal>
      <BasicModal show={showSectorModal} close={() => setShowSectorModal(false)} title="Nuevo sector" size="small">
        <SectorForm
          close={() => setShowSectorModal(false)}
          onReload={() => { setShowSectorModal(false); loadSectors(); }}
        />
      </BasicModal>
    </div>
  );
}
